import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  Copy, Check, Zap, ShieldAlert, ShieldCheck, ShieldX, X,
  FileText, Image as ImageIcon, FileAudio, Video as VideoIcon, File as FileIcon,
  Pencil, ExternalLink, Download, GitBranch, Volume2, VolumeX, MoonStar, ChevronUp,
  ChevronDown, ChevronRight,
} from 'lucide-react'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'
import {
  useMessages, useForceNow, useCancelQueuedMessage,
  useEditQueuedMessage, useRetryFailedMessage, useChat, messagesKey, preloadMessageReadModel,
  useChats, useForkMessage, expandMessageReadModel, MESSAGES_INITIAL_FETCH_LIMIT,
  clearExpandedMessageReadModel, getToolActivities, getToolActivityInput, refreshMessageReadModel,
  retainOpenMessageReadModel,
  type Message, type Attachment, type Chat, type ToolActivity,
} from '../lib/api'
import { attachmentDownloadHref, attachmentOpenPath, fetchAttachmentOpen, isAttachmentOpenFresh } from '../lib/attachmentDelivery'
import { draftTargetKey, useUi } from '../store/ui'
import { useElasticOverscroll } from '../lib/useElasticOverscroll'
import { HISTORY_FIRST_ITEM_INDEX, historyAfterReset, prependFirstItemIndex, watchUpwardHistoryScroll } from '../lib/topicHistoryScroll'
import { useLongPress } from '../lib/longPress'
import { chatTarget, X_LINKS_CHAT_ID } from '../lib/chatNavigation'
import { forkableLaneTargets, nextForkTopicTitle } from '../lib/forkChat'
import {
  isAgentChatNativeApp,
  isNativeExternalOpenAvailable,
  openNativeExternalUrl,
} from '../lib/nativeExternalOpen'
import {
  dayKey,
  dayLabel,
  fullDateTimeLabel,
  timeLabel,
} from '../lib/time'
import { parseApproval, type ParsedApproval } from '../lib/approvalPayload'
import { messageVisibleInConversation } from '../lib/messageVisibility'
import { writeClipboardText } from '../lib/clipboard'
import { outboxUploadPercent, retryOutbox, type OutboxUploadProgress } from '../lib/outbox'
import { useOutboxUploadProgress } from '../lib/outboxPending'
import { splitEmailDraftComposite } from '../lib/emailDraftCompositePayload'
import { splitPayCard } from '../lib/payCardPayload'
const EmailDraftCard = lazy(() => import('./EmailDraftCard'))
const EmailDraftFeedbackCard = lazy(() => import('./EmailDraftFeedbackCard'))
const StagedEmailDraftCard = lazy(() => import('./StagedEmailDraftCard'))
import { isQuestionCardAnswered, latestQuestionCardIndex, splitAskUserQuestion } from '../lib/askUserQuestionPayload'
const AskUserQuestionCard = lazy(() => import('./AskUserQuestionCard'))
const PayCard = lazy(() => import('./PayCard'))
import { splitAttachmentChips } from '../lib/attachmentChipsPayload'
const AttachmentCandidateChips = lazy(() => import('./AttachmentCandidateChips'))
import MarkdownBody from './MarkdownBody'
import CommitmentBoardMessage from './CommitmentBoardMessage'
import SnoozePicker from './SnoozePicker'
import CommitmentDenyDialog from './CommitmentDenyDialog'
import AgentBadge from './AgentBadge'
import { messageTextForReadAloud } from '../lib/readAloudText'
import { speakMessageAloud, stopReadAloud, useReadAloudState } from '../lib/readAloud'
import { finishNotificationOpenTiming } from '../lib/notificationOpenDiagnostics'
import { isCommitmentBoardMessage } from '../lib/commitmentBoard'
import {
  applyCommitmentAction,
  commitmentActionFromMessageAction,
  formatLocalDate,
  formatLocalDateTime,
} from '../lib/commitmentActions'
import { confirmTodoistClose } from '../lib/todoistCloseAlert'

const BOTTOM_ANCHOR_READY_GAP_PX = 2
const BOTTOM_ANCHOR_STABLE_FRAMES = 24
const BOTTOM_ANCHOR_MAX_ATTEMPTS = 60
const MESSAGE_TEXT_SELECTION_AUTO_SCROLL_PAUSE_MS = 4000
const TOOL_ACTIVITY_PAGE_SIZE = 50
const TARGET_SCROLL_MAX_ATTEMPTS = 12
const MESSAGE_SCROLLER_KEYBOARD_SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Spacebar',
])

const X_LINK_TOPIC_SOURCE_ACTION_KINDS = new Set([
  'close_topic',
  'remove_x_bookmark',
  'remove_x_bookmark_and_close',
])
const X_STATUS_URL_RE = /^https?:\/\/(?:x|twitter)\.com\/(?:i\/status\/|[A-Za-z0-9_]{2,40}\/status\/)\d{15,25}(?:[/?#].*)?$/i

function isMessageScrollerKeyboardScrollIntent(event: KeyboardEvent): boolean {
  const keyCanScrollMessageList = MESSAGE_SCROLLER_KEYBOARD_SCROLL_KEYS.has(event.key)
  const browserShortcutModifier = event.metaKey || event.ctrlKey || event.altKey
  return keyCanScrollMessageList && !browserShortcutModifier
}

function isMessageTextSelectionStart(event: Event): boolean {
  const selectionStartEvent =
    (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent) ||
    (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent)
  if (!selectionStartEvent) return false
  return event.target instanceof Element &&
    Boolean(event.target.closest('.agentchat-selectable-message-text'))
}

function nodeInsideSelectableMessageText(root: HTMLElement | null, node: Node | null): boolean {
  if (!root || !node || !root.contains(node)) return false
  const element = node instanceof Element ? node : node.parentElement
  return Boolean(element?.closest('.agentchat-selectable-message-text'))
}

function hasActiveMessageTextSelection(root: HTMLElement | null): boolean {
  if (typeof document === 'undefined') return false
  const selection = document.getSelection()
  if (!selection || selection.isCollapsed) return false
  return nodeInsideSelectableMessageText(root, selection.anchorNode) ||
    nodeInsideSelectableMessageText(root, selection.focusNode)
}

function messageStableKey(message: Message): string {
  return message.client_id ?? message.id
}

function messageHistoryScopeKey(chatId: string, topicId: string | null): string {
  return `${chatId}::${topicId ?? '__all__'}`
}

function scopeHasOlderMessages(
  messages: Message[],
  chatId: string,
  topicId: string | null,
): boolean {
  const first = messages[0]
  return first?.has_older_messages === true &&
    first.history_scope_key === messageHistoryScopeKey(chatId, topicId)
}

function isXLinkTopicSourceAction(action: NonNullable<Message['actions']>[number]): boolean {
  if (X_LINK_TOPIC_SOURCE_ACTION_KINDS.has(action.kind)) return true
  if (action.kind !== 'open_url') return false
  const url = String(action.payload?.url ?? '').trim()
  return X_STATUS_URL_RE.test(url)
}

function InlineMarkerCardShell({
  prefix,
  dialogueBubbleWidth,
  textSizeScale,
  attachments,
  children,
}: {
  prefix: string
  dialogueBubbleWidth: string
  textSizeScale: number
  attachments?: ReactNode
  children: ReactNode
}) {
  return (
    <>
      {(prefix || attachments) && (
        <div className="flex w-full justify-start">
          <div
            className={clsx(
              dialogueBubbleWidth,
              'agent-message-text agentchat-selectable-message-text',
              'px-3.5 py-2 my-0.5 break-words rounded-2xl shadow-sm bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 rounded-bl-md border border-stone-200/70 dark:border-stone-700',
            )}
            style={{ fontSize: `${14 * textSizeScale}px`, lineHeight: 1.45 }}
          >
            {prefix && <MarkdownBody body={prefix} />}
            {attachments}
          </div>
        </div>
      )}
      <Suspense fallback={
        <div className="px-3 py-2 my-0.5 rounded-2xl bg-white/60 dark:bg-stone-800/60 animate-pulse h-12 w-2/3 self-start" />
      }>
        {children}
      </Suspense>
    </>
  )
}

/* ── Approval-request bubble ────────────────────────────────────────────── */

function ApprovalBubble({
  approval, chatId, scale,
}: {
  approval: ParsedApproval
  chatId: string
  scale: number
}) {
  const { data: chat } = useChat(chatId)
  const laneId = chat?.lane_id ?? null
  const [pending, setPending] = useState<'approved' | 'approved_for_session' | 'denied' | null>(null)
  const [error, setError] = useState<string | null>(null)
  if (!approval) return null
  const isCredentialLogin = approval.kind === 'request' && approval.method === 'credential_login'

  if (approval.kind === 'responded') {
    const ok = approval.decision === 'approved' || approval.decision === 'approved_for_session'
    const Icon = ok ? ShieldCheck : ShieldX
    return (
      <div className="flex w-full justify-center my-2">
        <div className={clsx(
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs',
          ok
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
            : 'bg-stone-100 dark:bg-stone-900 text-stone-500 dark:text-stone-400 border border-stone-200 dark:border-stone-700',
        )}>
          <Icon className="w-3.5 h-3.5" />
          <span>{ok ? 'Approved' : 'Denied'} — {approval.summary}</span>
        </div>
      </div>
    )
  }

  async function respond(decision: 'approved' | 'approved_for_session' | 'denied') {
    if (!laneId && !isCredentialLogin) {
      setError('No lane bound to this chat')
      return
    }
    setPending(decision)
    setError(null)
    try {
      const endpoint = isCredentialLogin
        ? `/api/credential-approvals/${approval!.reqId}/respond`
        : `/api/approvals/${laneId}/${approval!.reqId}/respond`
      const r = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      })
      if (!r.ok) throw new Error(await r.text().catch(() => `HTTP ${r.status}`))
    } catch (e) {
      setError(String(e).slice(0, 200))
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="flex w-full justify-start my-2">
      <div
        className={clsx(
          'max-w-[88%] sm:max-w-[80%] rounded-2xl border-2 shadow-md',
          'border-amber-400 dark:border-amber-600',
          'bg-amber-50 dark:bg-amber-900/30',
        )}
        style={{ fontSize: `${14 * scale}px` }}
      >
        <div className="flex items-start gap-2 px-3.5 py-3">
          <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-amber-900 dark:text-amber-200">
              Approval needed
            </div>
            <div className="text-amber-800/80 dark:text-amber-300/80 mt-0.5 break-words">
              {approval.summary}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-amber-700/60 mt-1.5">
              {approval.method}
            </div>
            {error && (
              <div className="text-xs text-red-600 mt-1">{error}</div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 px-3 pb-3">
          <button
            onClick={() => respond('approved')}
            disabled={!!pending}
            className={clsx(
              'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium',
              'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
              'disabled:opacity-60 disabled:cursor-wait transition-colors',
            )}
          >
            <ShieldCheck className="w-4 h-4" />
            {pending === 'approved' ? 'Approving…' : (isCredentialLogin ? 'Allow domain' : 'Approve')}
          </button>
          {!isCredentialLogin && <button
            onClick={() => respond('approved_for_session')}
            disabled={!!pending}
            className={clsx(
              'inline-flex items-center justify-center px-2.5 py-2 rounded-xl text-xs',
              'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
              'hover:bg-emerald-200 dark:hover:bg-emerald-900/60',
              'disabled:opacity-60 transition-colors',
            )}
            title="Approve and don't ask again for the rest of this session"
          >
            {pending === 'approved_for_session' ? '…' : 'Always (session)'}
          </button>}
          <button
            onClick={() => respond('denied')}
            disabled={!!pending}
            className={clsx(
              'inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium',
              'bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-200',
              'border border-stone-300 dark:border-stone-600',
              'hover:bg-stone-100 dark:hover:bg-stone-700',
              'disabled:opacity-60 disabled:cursor-wait transition-colors',
            )}
          >
            <ShieldX className="w-4 h-4" />
            {pending === 'denied' ? 'Denying…' : 'Deny'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
/** Verdict-tag → friendly label + tone. Each topic type has its own
 * verdict enum (see `.claude/skills/topic-dispatch/SKILL.md`) — these
 * tags are the union across all types. Unknown tags fall through to a
 * stone-grey badge with the raw tag as label. */
function verdictDisplay(tag: string): { label: string; tone: string } {
  const t = tag.trim()
  // Positive / "ship now"
  if (t === 'useful_now' || t === 'EXECUTE-NOW' || t === 'fixed' ||
      t === 'drafted' || t === 'apply' || t === 'strong_match') {
    return { label: friendlyTag(t), tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' }
  }
  // Plan / pending — needs a follow-up
  if (t === 'useful_later' || t === 'PLAN-FIRST' || t === 'mitigated' ||
      t === 'investigating' || t === 'refine' || t === 'action_needed') {
    return { label: friendlyTag(t), tone: 'bg-amber-100 text-amber-800 border-amber-300' }
  }
  // Needs Mark's input
  if (t === 'CLARIFY' || t === 'needs_input' || t === 'blocked') {
    return { label: friendlyTag(t), tone: 'bg-blue-100 text-blue-800 border-blue-300' }
  }
  // Negative / archive
  if (t === 'not_useful' || t === 'DEFER' || t === 'reject' ||
      t === 'spam' || t === 'weak_match') {
    return { label: friendlyTag(t), tone: 'bg-stone-100 text-stone-700 border-stone-300' }
  }
  if (t === 'routine') {
    return { label: 'Routine', tone: 'bg-stone-100 text-stone-600 border-stone-300' }
  }
  return { label: t, tone: 'bg-stone-100 text-stone-700 border-stone-300' }
}

function friendlyTag(t: string): string {
  return t
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Tool-call progress indicator (audit item #10).
 *
 * The lane workers emit ``tool_call_progress`` journal events for every
 * tool_use block while the agent is mid-turn. ws.ts stashes the latest
 * line in the cache slot ['tool_progress', chatId, msgId]. This
 * component reads from that slot and renders a small rolling "🔧 ..."
 * indicator above the streaming bubble so Mark sees activity during
 * silent multi-min tool calls (web search, ripgrep, Bash).
 *
 * Auto-clears: on agent_reply (final body landed) the ws handler sets
 * the slot to null. Net behaviour: appears while the agent is working,
 * disappears the instant it's done.
 *
 * Cache-only (no network): queryFn returns null and enabled=false. The
 * only writes come from ws.ts's setQueryData calls. This pattern lets
 * us piggyback on TanStack Query's reactive cache without paying for
 * any HTTP round-trip.
 */
function ToolProgressIndicator({
  chatId,
  messageId,
}: {
  chatId: string
  messageId: string
}) {
  const { data } = useQuery<{ message_id: string; line: string; ts: number } | null>({
    queryKey: ['tool_progress', chatId, messageId],
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: null,
  })
  if (!data || !data.line) return null
  return (
    <div
      className={clsx(
        'mb-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full',
        'text-[10px] font-medium',
        'bg-blue-100 text-blue-800 border border-blue-200',
        'dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/40',
      )}
      title={data.line}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
      <span className="truncate max-w-[260px]">{data.line}</span>
    </div>
  )
}


/**
 * Tool calls the agent made while producing this reply — persisted, so they
 * survive the turn and a cold reload. Transcript rows carry only the count;
 * opening the group fetches its breadcrumbs, and opening one breadcrumb fetches
 * that exact call's FULL input. Heavy debug data never rides chat history.
 */
function ToolActivityRow({
  activity,
  chatId,
  messageId,
}: {
  activity: ToolActivity
  chatId: string
  messageId: string
}) {
  const [expanded, setExpanded] = useState(false)
  const legacyInputAvailable = activity.tool_input !== undefined
  const inputQuery = useQuery({
    queryKey: ['tool-activity-input', chatId, messageId, activity.seq],
    queryFn: () => getToolActivityInput(chatId, messageId, activity.seq),
    enabled: expanded && !legacyInputAvailable && activity.input_available !== false,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: 1,
  })
  const input = legacyInputAvailable
    ? activity.tool_input
    : inputQuery.data?.tool_input
  const full = input === undefined
    ? ''
    : typeof input === 'string'
      ? input
      : JSON.stringify(input, null, 2)

  return (
    <li className="border-b border-stone-200/70 last:border-b-0 dark:border-stone-800">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="block w-full px-2 py-1.5 text-left text-[12px] text-stone-700 dark:text-stone-200"
      >
        <span className={expanded ? '' : 'block truncate'}>{activity.summary}</span>
      </button>
      {expanded && inputQuery.isFetching && (
        <div className="mx-2 mb-2 text-[11px] text-stone-500 dark:text-stone-400">
          Loading full input…
        </div>
      )}
      {expanded && inputQuery.isError && (
        <div className="mx-2 mb-2 text-[11px] text-red-600 dark:text-red-300">
          Full input could not be loaded. Tap the call to close, then retry.
        </div>
      )}
      {expanded && activity.input_available === false && !legacyInputAvailable && (
        <div className="mx-2 mb-2 text-[11px] text-stone-500 dark:text-stone-400">
          No full input was recorded.
        </div>
      )}
      {expanded && full && (
        <pre className="mx-2 mb-2 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-stone-100 p-2 text-[11px] leading-snug text-stone-800 dark:bg-stone-950 dark:text-stone-200">
          {full}
        </pre>
      )}
    </li>
  )
}

function ToolActivities({
  activityCount,
  initialActivities,
  chatId,
  messageId,
}: {
  activityCount: number
  initialActivities: ToolActivity[]
  chatId: string
  messageId: string
}) {
  const [open, setOpen] = useState(false)
  const [legacyVisibleCount, setLegacyVisibleCount] = useState(TOOL_ACTIVITY_PAGE_SIZE)
  useEffect(() => setLegacyVisibleCount(TOOL_ACTIVITY_PAGE_SIZE), [messageId])
  const summariesQuery = useInfiniteQuery({
    queryKey: ['tool-activities', chatId, messageId],
    queryFn: ({ pageParam }) => getToolActivities(
      chatId,
      messageId,
      pageParam,
      TOOL_ACTIVITY_PAGE_SIZE,
    ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (
      lastPage.has_more ? lastPage.next_after_seq : undefined
    ),
    enabled: open && initialActivities.length === 0,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: 1,
  })
  const activities = initialActivities.length > 0
    ? initialActivities.slice(0, legacyVisibleCount)
    : (summariesQuery.data?.pages.flatMap((page) => page.activities) ?? [])
  const hasMoreActivities = initialActivities.length > 0
    ? legacyVisibleCount < initialActivities.length
    : summariesQuery.hasNextPage === true
  const loadMoreActivities = () => {
    if (initialActivities.length > 0) {
      setLegacyVisibleCount((count) => count + TOOL_ACTIVITY_PAGE_SIZE)
      return
    }
    void summariesQuery.fetchNextPage()
  }
  if (activityCount === 0) return null
  return (
    <div className="mb-1.5 rounded-lg border border-stone-200 bg-stone-50/70 dark:border-stone-700 dark:bg-stone-900/40">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[11px] font-medium text-stone-600 dark:text-stone-300"
      >
        {open
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />}
        {activityCount} tool {activityCount === 1 ? 'call' : 'calls'}
      </button>
      {open && summariesQuery.isFetching && activities.length === 0 && (
        <div className="border-t border-stone-200 px-2 py-2 text-[11px] text-stone-500 dark:border-stone-700 dark:text-stone-400">
          Loading tool calls…
        </div>
      )}
      {open && summariesQuery.isError && (
        <div className="border-t border-stone-200 px-2 py-2 text-[11px] text-red-600 dark:border-stone-700 dark:text-red-300">
          Tool calls could not be loaded. Close, then retry.
        </div>
      )}
      {open && activities.length > 0 && (
        <>
          <ul className="max-h-96 overflow-y-auto border-t border-stone-200 dark:border-stone-700">
            {activities.map((activity) => (
              <ToolActivityRow
                key={activity.seq}
                activity={activity}
                chatId={chatId}
                messageId={messageId}
              />
            ))}
          </ul>
          <div className="flex items-center justify-between gap-2 border-t border-stone-200 px-2 py-1.5 text-[11px] text-stone-500 dark:border-stone-700 dark:text-stone-400">
            <span>Showing {activities.length} of {activityCount}</span>
            {hasMoreActivities && (
              <button
                type="button"
                onClick={loadMoreActivities}
                disabled={summariesQuery.isFetchingNextPage}
                className="min-h-8 rounded-md border border-stone-300 bg-white px-2 font-medium text-stone-700 active:bg-stone-100 disabled:opacity-60 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200"
              >
                {summariesQuery.isFetchingNextPage ? 'Loading…' : 'Load 50 more'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function VerdictBadge({ verdict }: { verdict: string }) {
  const { label, tone } = verdictDisplay(verdict)
  return (
    <div className={clsx(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border mb-1.5',
      tone,
    )}
    aria-label={`Verdict: ${label}`}>
      {label}
    </div>
  )
}

function ActionChip({
  action, chatId, messageId, topicId,
}: {
  action: NonNullable<Message['actions']>[number]
  chatId: string
  messageId: string
  topicId: string | null
}) {
  const [state, setState] = useState<'idle' | 'pending' | 'done' | 'error'>('idle')
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [showSnoozePicker, setShowSnoozePicker] = useState(false)
  const [showDenyReason, setShowDenyReason] = useState(false)
  const qc = useQueryClient()
  const commitmentAction = commitmentActionFromMessageAction(action)
  const topicSnooze = !commitmentAction && action.kind === 'snooze_7d' && !!topicId
  const opensSnoozePicker = !!commitmentAction?.picker || topicSnooze
  const displayLabel = topicSnooze ? 'Snooze' : action.label

  async function submitCommitmentAction(reason?: string) {
    if (!commitmentAction) return
    await applyCommitmentAction({
      ...commitmentAction,
      reason,
      chatId: commitmentAction.action === 'accept' ? chatId : undefined,
      topicId: commitmentAction.action === 'accept' ? topicId ?? undefined : undefined,
    })
    setState('done')
    refreshMessageReadModel(qc, chatId, topicId)
    if (topicId) refreshMessageReadModel(qc, chatId, null)
    qc.invalidateQueries({ queryKey: ['topics', chatId] })
    qc.invalidateQueries({ queryKey: ['commitments-dashboard'] })
  }

  async function onClick() {
    if (state === 'pending' || state === 'done') return
    if (opensSnoozePicker) {
      setShowSnoozePicker(true)
      return
    }
    if (commitmentAction?.action === 'deny') {
      setShowDenyReason(true)
      return
    }
    setState('pending')
    setErrMsg(null)
    try {
      if (commitmentAction) {
        await submitCommitmentAction()
        return
      }
      if (action.kind === 'close_todoist_task') {
        if (!topicId) throw new Error('Todoist close action has no topic')
        const confirmed = await confirmTodoistClose({ topicIds: [topicId] })
        if (!confirmed) {
          setState('idle')
          return
        }
      }
      const r = await fetch(`/api/messages/${messageId}/action`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: action.id,
          label: action.label,
          kind: action.kind,
          payload: action.payload ?? null,
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const j = await r.json().catch(() => ({}))
      if (j.status === 'failed') throw new Error(j.error || 'failed')
      setState('done')

      // Inject a right-side user bubble so the tap is visible in the chat,
      // using the durable DB-assigned ID the backend just returned.
      const echoId = String(j.echo_message_id || `echo-${messageId}-${action.id}`)
      const echo: Message = {
        id: echoId,
        client_id: echoId,
        topic_id: topicId,
        external_message_id: null,
        direction: 'inbound',
        user: 'mark',
        ts: Math.floor(Date.now() / 1000),
        body: action.label,
        status: 'received',
        queue_position: null,
        force_dispatch: false,
        reply_to_message_id: null,
        unread: false,
        deleted: false,
      }
      const keys = [messagesKey(chatId, topicId)]
      if (topicId) keys.push(messagesKey(chatId, null))
      for (const key of keys) {
        qc.setQueryData<Message[]>(key, (prev) => {
          const list = prev ?? []
          if (list.some((m) => m.id === echoId || m.client_id === echoId)) return list
          return [...list, echo]
        })
      }
    } catch (e) {
      setState('error')
      setErrMsg(String(e).slice(0, 200))
    }
  }

  async function applyPickedSnooze(wakeAtUnix: number) {
    setShowSnoozePicker(false)
    if (state === 'pending' || state === 'done') return
    setState('pending')
    setErrMsg(null)
    try {
      if (commitmentAction) {
        const picked = new Date(wakeAtUnix * 1000)
        await applyCommitmentAction({
          ...commitmentAction,
          value: commitmentAction.action === 'setfu'
            ? formatLocalDateTime(picked)
            : formatLocalDate(picked),
        })
      } else if (topicSnooze && topicId) {
        const r = await fetch(`/api/chats/${encodeURIComponent(chatId)}/topics/${encodeURIComponent(topicId)}/snooze`, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wake_at: wakeAtUnix }),
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
      }
      setState('done')
      refreshMessageReadModel(qc, chatId, topicId)
      if (topicId) refreshMessageReadModel(qc, chatId, null)
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['commitments-dashboard'] })
    } catch (e) {
      setState('error')
      setErrMsg(String(e).slice(0, 200))
    }
  }

  const visualState =
    state === 'done' ? 'done' :
    state === 'error' ? 'error' :
    state === 'pending' ? 'pending' : 'idle'
  const isCloseAction = action.kind === 'close_topic'
    || action.kind === 'close_todoist_task'
    || action.kind === 'remove_x_bookmark_and_close'
  const isApproveAction = commitmentAction?.action === 'done'
    || commitmentAction?.action === 'keep'
    || commitmentAction?.action === 'accept'
  const isRejectAction = commitmentAction?.action === 'deny'
  const isDestructiveAction = action.kind === 'remove_x_bookmark'
  // 'chase' was dropped from CommitmentActionName (it's now in the backend's
  // DISABLED_COMMITMENT_ACTIONS); the dangling compare left tsc red and broke
  // every PWA build. Run-styling now covers only the live run-type actions.
  const isRunAction = action.kind.startsWith('run_skill:')
    || action.kind === 'recheck_now'
  const isOpenUrlAction = action.kind === 'open_url'
  const openUrl = isOpenUrlAction ? String(action.payload?.url ?? '') : ''
  const openExpiresAt = isOpenUrlAction ? Number(action.payload?.expires_at ?? 0) : 0
  const openUrlExpired = Number.isFinite(openExpiresAt)
    && openExpiresAt > 0
    && openExpiresAt <= Math.floor(Date.now() / 1000)
  const idleTone = isApproveAction || isCloseAction
    ? [
        'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700 shadow-emerald-900/20',
        'dark:bg-emerald-500 dark:hover:bg-emerald-400 dark:text-emerald-950 dark:border-emerald-300 dark:shadow-emerald-950/30',
      ]
    : isRejectAction || isDestructiveAction
      ? [
          'bg-red-600 hover:bg-red-700 text-white border-red-700 shadow-red-900/20',
          'dark:bg-red-500 dark:hover:bg-red-400 dark:text-red-950 dark:border-red-300 dark:shadow-red-950/30',
        ]
      : action.kind.startsWith('snooze_')
        ? [
            'bg-amber-500 hover:bg-amber-600 text-amber-950 border-amber-600 shadow-amber-900/20',
            'dark:bg-amber-400 dark:hover:bg-amber-300 dark:text-amber-950 dark:border-amber-200 dark:shadow-amber-950/30',
          ]
        : [
            'bg-blue-600 hover:bg-blue-700 text-white border-blue-700 shadow-blue-900/20',
            'dark:bg-sky-400 dark:hover:bg-sky-300 dark:text-sky-950 dark:border-sky-200 dark:shadow-sky-950/30',
          ]

  const baseClasses = clsx(
    'inline-flex min-h-8 items-center gap-1.5 rounded-full px-3 py-1.5',
    'text-[12px] font-semibold leading-none shadow-sm transition no-underline',
    'border-2 active:scale-95',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
    visualState === 'idle' && idleTone,
    visualState === 'pending' && [
      'bg-blue-100 text-blue-800 border-blue-300 cursor-wait opacity-90',
      'dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700',
    ],
    visualState === 'done' && [
      'bg-emerald-100 text-emerald-800 border-emerald-300 cursor-default',
      'dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700',
    ],
    visualState === 'error' && [
      'bg-red-100 text-red-800 border-red-300',
      'dark:bg-red-900/40 dark:text-red-200 dark:border-red-700',
    ],
  )

  if (isOpenUrlAction && openUrl) {
    if (openUrlExpired) {
      return (
        <span
          className={clsx(
            'inline-flex min-h-8 items-center gap-1.5 rounded-full px-3 py-1.5',
            'text-[12px] font-semibold leading-none no-underline border-2',
            'cursor-not-allowed border-stone-300 bg-stone-100 text-stone-500 shadow-none',
            'dark:border-stone-600 dark:bg-stone-800 dark:text-stone-400',
          )}
          title={`${displayLabel} expired after this event date`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {action.id === 'open_tickets' ? 'Past event' : displayLabel}
        </span>
      )
    }
    // Pure link — open natively, no server roundtrip. We still POST a
    // best-effort journal so taps appear in the action trail like any
    // other chip; ignore failures.
    function onAnchorClick() {
      void fetch(`/api/messages/${messageId}/action`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: action.id, label: action.label, kind: action.kind,
          payload: action.payload ?? null,
        }),
      }).catch(() => {})
    }
    return (
      <a
        href={openUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onAnchorClick}
        className={baseClasses}
        title={`${action.kind} — ${openUrl}`}
      >
        <ExternalLink className="w-3.5 h-3.5" />
        {displayLabel}
      </a>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={state === 'pending' || state === 'done'}
        className={baseClasses}
        title={errMsg || (opensSnoozePicker ? `${displayLabel} - choose date/time` : `${action.kind} - id=${action.id}`)}
      >
        {visualState === 'pending' && <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />}
        {visualState === 'done' && <Check className="w-3 h-3" />}
        {visualState === 'idle' && isRunAction && <Zap className="w-3.5 h-3.5" />}
        {visualState === 'idle' && opensSnoozePicker && <MoonStar className="w-3.5 h-3.5" />}
        {visualState === 'idle' && isCloseAction && <Check className="w-3.5 h-3.5" />}
        {visualState === 'idle' && (isRejectAction || isDestructiveAction) && <X className="w-3.5 h-3.5" />}
        {visualState === 'done' ? 'Done' : displayLabel}
      </button>
      {showDenyReason && commitmentAction?.action === 'deny' && (
        <CommitmentDenyDialog
          disabled={state === 'pending'}
          onCancel={() => setShowDenyReason(false)}
          onSubmit={(reason) => {
            setShowDenyReason(false)
            setState('pending')
            setErrMsg(null)
            void submitCommitmentAction(reason).catch((e) => {
              setState('error')
              setErrMsg(String(e).slice(0, 200))
            })
          }}
        />
      )}
      {showSnoozePicker && (
        <SnoozePicker
          title={
            commitmentAction?.action === 'setdue'
              ? 'Set promised date'
              : commitmentAction?.action === 'setfu'
                ? 'Set follow-up date & time'
                : commitmentAction
                  ? 'Snooze commitment'
                  : 'Snooze topic'
          }
          footerNote={
            commitmentAction?.action === 'setdue'
              ? 'Sets the promised date this person owes you by.'
              : commitmentAction?.action === 'setfu'
                ? 'When the tracker should bring this commitment back if still unresolved.'
                : commitmentAction
                  ? 'Hides this commitment until the chosen day, then it returns to the active list.'
              : 'Hides this topic until the chosen time.'
          }
          onCancel={() => setShowSnoozePicker(false)}
          onPick={applyPickedSnooze}
        />
      )}
    </>
  )
}


/* ── Day-separator pills (WhatsApp-style) ──────────────────────────────────
 * Without these, all you see beside each bubble is HH:mm — no way to tell
 * whether 05:46 was today or three days ago. We interleave a centered pill
 * at every day boundary in the visible list, plus widen the bubble's own
 * timestamp tooltip to the full date so a tap reveals the exact moment.
 */

type ListItem =
  | { kind: 'separator'; id: string; label: string }
  | { kind: 'message'; m: Message }

function buildListItems(msgs: Message[]): ListItem[] {
  const out: ListItem[] = []
  let lastDayKey: string | null = null
  for (const m of msgs) {
    const key = dayKey(m.ts)
    if (key !== lastDayKey) {
      out.push({ kind: 'separator', id: `sep-${key}`, label: dayLabel(m.ts) })
      lastDayKey = key
    }
    out.push({ kind: 'message', m })
  }
  return out
}

function DaySeparator({ label }: { label: string }) {
  return (
    <div className="flex w-full justify-center my-2">
      <div
        className={clsx(
          'px-2.5 py-1 rounded-md text-[11px] font-medium uppercase tracking-wide',
          'bg-stone-200/80 text-stone-600',
          'dark:bg-stone-800/80 dark:text-stone-400',
          'shadow-sm',
        )}
      >
        {label}
      </div>
    </div>
  )
}

function uploadProgressLabel(progress: OutboxUploadProgress): string {
  const percent = `${outboxUploadPercent(progress)}%`
  return progress.files > 1
    ? `Uploading ${progress.file} of ${progress.files} · ${percent}`
    : `Uploading · ${percent}`
}

/**
 * Single message bubble.
 *
 * - Touch context-menu on mobile / CSS hover on desktop reveals the action
 *   chips (copy / fork / read-aloud). Laptop mouse/trackpad text selection
 *   stays a native browser path: no message long-press and no hover mount
 *   re-render while the drag is starting.
 * - Optimistic ``temp-…`` ids show a single-tick "sending" marker; once
 *   the server replaces them, the marker becomes a double-tick.
 * - ``status='error'`` bubbles flash red with retry hint.
 */
// Memoized: `renderMessageItem`'s identity churns on every streaming delta
// (activeTopicIds/latestAgentId/items.length all change), so without this
// Virtuoso re-runs itemContent for every visible row and each MessageBubble
// re-parses its markdown + re-lays-out attachments on every delta — the
// visible "flicker" during an agent reply. mergeVisibleMessages preserves
// object identity for unchanged messages (only the changed record is
// reallocated), and every prop here is referentially stable when unchanged
// (`m` ref, string chatId, booleans, and the stable setOpenImage/openForkDialog
// callbacks), so a default shallow memo re-renders ONLY the bubble that changed.
const MessageBubble = memo(function MessageBubble({
  m, chatId, isLatestAgent, answeredLater, replacedLater, laneIsBusy, topicHasActiveTurn, onOpenImage, onFork,
}: {
  m: Message
  chatId: string
  isLatestAgent: boolean
  /** Mark has sent a message after this one (closes question cards). */
  answeredLater: boolean
  /** A newer question card follows this one (replaces an unanswered card). */
  replacedLater: boolean
  laneIsBusy: boolean
  topicHasActiveTurn: boolean
  onOpenImage: (att: Attachment) => void
  onFork: (message: Message) => void
}) {
  const forceNow = useForceNow(chatId)
  const cancelQueued = useCancelQueuedMessage(chatId)
  const editQueued = useEditQueuedMessage(chatId)
  const retryFailed = useRetryFailedMessage(chatId)
  const qc = useQueryClient()
  const textSizeScale = useUi((s) => s.textSizeScale)
  const useFullWindowWidth = useUi((s) => s.chatFullWindow && s.effectiveWidth >= 560)
  const setDraft = useUi((s) => s.setDraft)
  const readAloudSpeed = useUi((s) => s.readAloudSpeed)
  const readAloudVoice = useUi((s) => s.readAloudVoice)
  const isInbound = m.direction === 'inbound'
  const isAgent = m.direction === 'outbound' || m.direction === 'agent'
  const isSystem = m.direction === 'system'
  const toolActivityCount = m.activity_count ?? m.activities?.length ?? 0
  const isOptimistic = m.id.startsWith('temp-') || (
    m.outbox_pending === true && m.status === 'queued' && !m.outbox_error
  )
  const isQueued = m.status === 'queued'
  const [showActions, setShowActions] = useState(false)
  const [actionsPinned, setActionsPinned] = useState(false)
  const [queuedEditText, setQueuedEditText] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const bubbleRef = useRef<HTMLDivElement | null>(null)
  const mouseTextSelectionActiveRef = useRef(false)
  const stopMouseTextSelectionTrackingRef = useRef<(() => void) | null>(null)
  const readAloud = useReadAloudState()

  const actionsVisible = showActions || actionsPinned
  const useActionLongPress = isInbound || isAgent
  const uploadProgress = useOutboxUploadProgress(m.outbox_pending === true ? m.id : null)
  const canRetry = isInbound && m.status === 'error' && !uploadProgress
  const [retryingOutbox, setRetryingOutbox] = useState(false)
  const retryingThisMessage = retryingOutbox || (retryFailed.isPending && retryFailed.variables === m.id)
  const canReadAloud = useMemo(() => messageTextForReadAloud(m.body).length > 0, [m.body])
  const visibleMessageActions = useMemo(
    () => (m.actions ?? []).filter((action) => {
      if (action.kind === 'commitment_action') return false
      if (chatId === X_LINKS_CHAT_ID && m.topic_id && isXLinkTopicSourceAction(action)) {
        return false
      }
      return true
    }),
    [chatId, m.actions, m.topic_id],
  )
  const readingThisMessage =
    readAloud.messageId === m.id &&
    (readAloud.status === 'loading' || readAloud.status === 'playing')
  const dialogueBubbleWidth = useFullWindowWidth && !isInbound
    ? 'max-w-full'
    : 'max-w-[88%] sm:max-w-[72%]'
  const longPressBind = useLongPress(() => {
    setActionsPinned(true)
    setShowActions(true)
  }, { delay: 450, moveTolerance: 20, allowTextSelection: true })

  const clearMouseTextSelectionTracking = useCallback(() => {
    mouseTextSelectionActiveRef.current = false
    if (stopMouseTextSelectionTrackingRef.current) {
      stopMouseTextSelectionTrackingRef.current()
      stopMouseTextSelectionTrackingRef.current = null
    }
  }, [])

  const beginMouseTextSelectionTracking = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return
    const target = e.target
    if (!(target instanceof Element)) return
    if (!target.closest('.agentchat-selectable-message-text')) return

    if (stopMouseTextSelectionTrackingRef.current) {
      stopMouseTextSelectionTrackingRef.current()
      stopMouseTextSelectionTrackingRef.current = null
    }

    mouseTextSelectionActiveRef.current = true
    const stopTracking = () => clearMouseTextSelectionTracking()
    document.addEventListener('pointerup', stopTracking, { once: true })
    document.addEventListener('pointercancel', stopTracking, { once: true })
    stopMouseTextSelectionTrackingRef.current = () => {
      document.removeEventListener('pointerup', stopTracking)
      document.removeEventListener('pointercancel', stopTracking)
    }
  }, [clearMouseTextSelectionTracking])

  useEffect(() => () => clearMouseTextSelectionTracking(), [clearMouseTextSelectionTracking])

  function handleMessageContextMenu(e: MouseEvent<HTMLDivElement>) {
    if (!useActionLongPress) return
    const pointerType = (e.nativeEvent as PointerEvent).pointerType
    const desktopMouseMenu =
      pointerType === 'mouse' ||
      (!pointerType && window.matchMedia('(hover: hover) and (pointer: fine)').matches)
    if (desktopMouseMenu) return
    longPressBind.onContextMenu?.(e)
  }

  useEffect(() => {
    if (!actionsPinned) return
    function onOutsideClick(e: PointerEvent) {
      if (!bubbleRef.current?.contains(e.target as Node)) {
        setActionsPinned(false)
        setShowActions(false)
      }
    }
    // Use click (not pointerdown) so that scroll gestures don't dismiss the
    // menu — click only fires after a complete tap with no significant movement.
    document.addEventListener('click', onOutsideClick, true)
    return () => document.removeEventListener('click', onOutsideClick, true)
  }, [actionsPinned])

  async function copyText() {
    if (await writeClipboardText(m.body)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  function toggleReadAloud() {
    if (readingThisMessage) {
      stopReadAloud()
      return
    }
    void speakMessageAloud({
      messageId: m.id,
      body: m.body,
      source: 'manual',
      speed: readAloudSpeed,
      voice: readAloudVoice,
    })
  }

  async function cancelQueuedMessage() {
    try {
      const cancelled = await cancelQueued.mutateAsync(m.id)
      setDraft(draftTargetKey(chatId, cancelled.topic_id), cancelled.body)
      window.dispatchEvent(new CustomEvent('agentchat:focus-composer', {
        detail: { chatId, draftKey: draftTargetKey(chatId, cancelled.topic_id) },
      }))
    } catch {/* handled by query mutation state */}
  }

  async function saveQueuedEdit() {
    const next = (queuedEditText ?? '').trim()
    if (!next || editQueued.isPending) return
    try {
      await editQueued.mutateAsync({ message_id: m.id, text: next })
      setQueuedEditText(null)
    } catch {
      // Mutation state carries the error; keep the editor open.
    }
  }

  async function retryFailedMessage() {
    if (!canRetry || retryingThisMessage || actionsPinned) return
    if (!m.outbox_pending) {
      retryFailed.mutate(m.id)
      return
    }
    setRetryingOutbox(true)
    try {
      await retryOutbox(m.id)
      await refreshMessageReadModel(qc, chatId, m.topic_id ?? null)
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['chat', chatId] })
    } finally {
      setRetryingOutbox(false)
    }
  }

  const messageAttachments = m.attachments?.length ? (
    <MessageAttachments attachments={m.attachments} isInbound={isInbound} onOpenImage={onOpenImage} />
  ) : null

  // Approval-request system messages render as a prominent action card
  // with inline Approve/Deny buttons. Mark explicitly asked for this:
  // when an agent asks permission, show the choices in the chat the same
  // way Telegram bot inline keyboards do.
  const approval = isSystem ? parseApproval(m.body) : null
  if (approval) {
    return (
      <ApprovalBubble
        approval={approval}
        chatId={chatId}
        scale={textSizeScale}
      />
    )
  }

  // Email-drafter bundle — the agent may emit both a tweak-feedback marker
  // and an Outlook draft marker in either order. The UI always renders the
  // questions first so Mark can decide how to change the draft before reading
  // or sending it.
  const emailDraft = m.status === 'received' ? splitEmailDraftComposite(m.body) : null
  if (emailDraft) {
    return (
      <InlineMarkerCardShell
        prefix={emailDraft.prefix}
        dialogueBubbleWidth={dialogueBubbleWidth}
        textSizeScale={textSizeScale}
        attachments={messageAttachments}
      >
        {emailDraft.feedback && (
          <EmailDraftFeedbackCard
            payload={emailDraft.feedback}
            chatId={chatId}
            topicId={m.topic_id}
          />
        )}
        {emailDraft.draft && (
          <EmailDraftCard
            payload={emailDraft.draft}
            chatId={chatId}
            topicId={m.topic_id}
          />
        )}
        {emailDraft.stagedDraft && (
          <StagedEmailDraftCard
            payload={emailDraft.stagedDraft}
            chatId={chatId}
            topicId={m.topic_id}
          />
        )}
      </InlineMarkerCardShell>
    )
  }

  // AskUserQuestion card — agent emits `__ASK_USER_QUESTION__{json}` when
  // Claude's AskUserQuestion tool fires. Renders tappable option buttons
  // so the user can unblock the lane turn from the PWA.
  // Any text before the marker is rendered as a normal agent bubble first.
  const askQuestion = splitAskUserQuestion(m.body)
  if (askQuestion) {
    return (
      <InlineMarkerCardShell
        prefix={askQuestion.prefix}
        dialogueBubbleWidth={dialogueBubbleWidth}
        textSizeScale={textSizeScale}
        attachments={messageAttachments}
      >
        <AskUserQuestionCard
          messageId={m.id}
          payload={askQuestion.payload}
          chatId={chatId}
          topicId={m.topic_id}
          answeredLater={answeredLater}
          replacedLater={replacedLater}
        />
      </InlineMarkerCardShell>
    )
  }

  // Pay card — the build-cost tracker emits `__PAY_CARD__{json}` when Mark
  // asks to pay a supplier. Copy chips + Open Revolut / Open AIB; the prose
  // before the marker renders as a normal agent bubble first.
  const payCard = m.status === 'received' ? splitPayCard(m.body) : null
  if (payCard) {
    return (
      <InlineMarkerCardShell
        prefix={payCard.prefix}
        dialogueBubbleWidth={dialogueBubbleWidth}
        textSizeScale={textSizeScale}
        attachments={messageAttachments}
      >
        <PayCard payload={payCard.payload} />
      </InlineMarkerCardShell>
    )
  }

  // Attachment candidates — agent emits `__ATTACHMENT_CHIPS__{json}` after
  // running the attachment_finder helper. Renders chips with Keep/Drop/+Add.
  // Any text before the marker is rendered as a normal agent bubble first.
  const attachChips = splitAttachmentChips(m.body)
  if (attachChips) {
    return (
      <InlineMarkerCardShell
        prefix={attachChips.prefix}
        dialogueBubbleWidth={dialogueBubbleWidth}
        textSizeScale={textSizeScale}
        attachments={messageAttachments}
      >
        <AttachmentCandidateChips payload={attachChips.payload} />
      </InlineMarkerCardShell>
    )
  }

  // System messages render as a small centered pill (Telegram-style "context"
  // banner), NOT a bubble. They're informational, not part of the dialogue.
  if (isSystem) {
    return (
      <div className="flex w-full justify-center my-2">
        <div
          className={clsx(
            'max-w-[88%] px-3 py-1.5 rounded-full',
            'bg-stone-200/70 dark:bg-stone-800/70 text-stone-600 dark:text-stone-400',
            'text-xs leading-snug text-center',
          )}
          style={{ fontSize: `${12 * textSizeScale}px` }}
        >
          {m.body}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={bubbleRef}
      className={clsx('agentchat-message-row group flex w-full', isInbound ? 'justify-end' : 'justify-start')}
      onPointerDown={(e) => {
        beginMouseTextSelectionTracking(e)
        if (useActionLongPress && e.pointerType !== 'mouse') longPressBind.onPointerDown(e)
      }}
      onPointerMove={(e) => {
        if (useActionLongPress && e.pointerType !== 'mouse') longPressBind.onPointerMove(e)
      }}
      onPointerUp={(e) => {
        if (e.pointerType === 'mouse') clearMouseTextSelectionTracking()
        if (useActionLongPress && e.pointerType !== 'mouse') longPressBind.onPointerUp(e)
      }}
      onPointerCancel={(e) => {
        if (e.pointerType === 'mouse') clearMouseTextSelectionTracking()
        if (useActionLongPress && e.pointerType !== 'mouse') longPressBind.onPointerCancel(e)
      }}
      onPointerLeave={(e) => {
        if (useActionLongPress && e.pointerType !== 'mouse') longPressBind.onPointerLeave(e)
        if (!actionsPinned && !mouseTextSelectionActiveRef.current) setShowActions(false)
      }}
      onContextMenu={handleMessageContextMenu}
      onClick={canRetry ? retryFailedMessage : undefined}
      onKeyDown={canRetry ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          retryFailedMessage()
        }
      } : undefined}
      role={canRetry ? 'button' : undefined}
      tabIndex={canRetry ? 0 : undefined}
      aria-label={canRetry ? 'Retry failed message' : undefined}
    >
      {/* Action chips on the OPPOSITE side of the bubble for hand-friendly thumb reach. */}
      {isInbound && actionsVisible && !isOptimistic && (
        <MessageActions
          className="agentchat-message-actions-touch"
          onCopy={copyText}
          copied={copied}
          onFork={() => onFork(m)}
          onReadAloud={toggleReadAloud}
          canReadAloud={canReadAloud}
          reading={readingThisMessage}
        />
      )}
      {isInbound && !isOptimistic && (
        <MessageActions
          className="agentchat-message-actions-desktop-reserved"
          onCopy={copyText}
          copied={copied}
          onFork={() => onFork(m)}
          onReadAloud={toggleReadAloud}
          canReadAloud={canReadAloud}
          reading={readingThisMessage}
        />
      )}

      <div
        className={clsx(
          'relative px-3.5 py-2 my-0.5 break-words rounded-2xl shadow-sm',
          dialogueBubbleWidth,
          isInbound
            ? 'bg-blue-600 text-white rounded-br-md'
            : isAgent
              ? 'bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 rounded-bl-md border border-stone-200/70 dark:border-stone-700'
              : 'bg-stone-100/80 dark:bg-stone-900/50 text-stone-600 dark:text-stone-400 italic rounded-xl',
          isOptimistic && 'opacity-80',
          m.status === 'error' && !uploadProgress && 'ring-2 ring-red-400',
          canRetry && 'cursor-pointer',
        )}
        data-agentchat-message-bubble={isInbound ? 'inbound' : isAgent ? 'agent' : 'system'}
        style={{ fontSize: `${14 * textSizeScale}px`, lineHeight: 1.45 }}
      >
        {/* Tool-call progress chip — gated on TWO conditions so it's a
            pure function of server-truth, never a stale leftover:
              1. This bubble is the LATEST agent bubble in the chat. A
                 prior turn's bubble can never light up its chip again,
                 even if its cache slot is orphaned (e.g. agent_reply
                 was lost to WS).
              2. The chat's lane is currently `busy`. When the lane
                 crashes, idles, or restarts, the chip vanishes
                 immediately on the next chats refetch — no TTL guess.
            Together: the chip is visible iff the lane is actively
            working on the latest agent reply for this chat. */}
        {isAgent && isLatestAgent && laneIsBusy && (
          <ToolProgressIndicator chatId={chatId} messageId={m.id} />
        )}
        {/* Verdict badge — small chip above the body, only on agent
            replies that came back through the topic_dispatch lib with
            a parsed verdict tag. The label maps verdict → human text;
            colour reflects whether the verdict implies a green/amber/
            red outcome. */}
        {isAgent && m.verdict && (
          <VerdictBadge verdict={m.verdict} />
        )}
        {isAgent && toolActivityCount > 0 && (
          <ToolActivities
            activityCount={toolActivityCount}
            initialActivities={m.activities ?? []}
            chatId={chatId}
            messageId={m.id}
          />
        )}
        {/* Body. Agent / system replies render as markdown so
            code fences, bullets, headings, and **emphasis** appear
            properly instead of raw text — Codex/Claude both emit
            heavy markdown. Inbound (Mark's own messages) stays
            plain since he's typing free-form, not authoring docs. */}
        {isInbound && isQueued && queuedEditText !== null ? (
          <div
            className="min-w-[220px] max-w-full"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <textarea
              value={queuedEditText}
              onChange={(e) => setQueuedEditText(e.target.value)}
              className={clsx(
                'w-full min-h-20 rounded-xl border px-2.5 py-2',
                'bg-white text-stone-900 border-blue-200',
                'focus:outline-none focus:ring-2 focus:ring-white/70',
              )}
              autoFocus
            />
            {editQueued.isError && (
              <div className="mt-1 text-[11px] text-red-100">
                Edit failed: {String((editQueued.error as Error).message)}
              </div>
            )}
            <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
              <button
                type="button"
                onClick={() => setQueuedEditText(null)}
                disabled={editQueued.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-blue-500/30 px-2 py-1 text-[11px] font-medium text-blue-50"
              >
                <X className="w-3 h-3" />
                Keep
              </button>
              <button
                type="button"
                onClick={() => void saveQueuedEdit()}
                disabled={!queuedEditText.trim() || editQueued.isPending}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-md px-2 py-1',
                  'text-[11px] font-medium',
                  'bg-white text-blue-700 disabled:opacity-60 disabled:cursor-wait',
                )}
              >
                <Check className="w-3 h-3" />
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Action buttons rendered ABOVE the body so they sit at the
                top of the bubble — same pattern as Todoist topic chips —
                and stay reachable on long messages without scrolling. */}
            {isAgent && visibleMessageActions.length > 0 && (
              <div className="mb-2.5 flex flex-wrap gap-2">
                {visibleMessageActions.map((a) => (
                  <ActionChip key={a.id} action={a} chatId={chatId}
                              messageId={m.id} topicId={m.topic_id} />
                ))}
              </div>
            )}
            {isInbound ? (
              <div className="agentchat-selectable-message-text whitespace-pre-wrap">{m.body}</div>
            ) : (
              <div
                className={isAgent ? 'agent-message-text agentchat-selectable-message-text' : 'agentchat-selectable-message-text'}
              >
                {isCommitmentBoardMessage(chatId, m.body) ? (
                  <CommitmentBoardMessage
                    body={m.body}
                    chatId={chatId}
                    topicId={m.topic_id}
                  />
                ) : (
                  <MarkdownBody body={m.body} />
                )}
              </div>
            )}
          </>
        )}
        {messageAttachments}
        <div className={clsx(
          'flex items-center gap-1.5 text-[10px] mt-1.5',
          isInbound ? 'text-blue-100/80' : 'text-stone-500',
        )}>
          <span title={fullDateTimeLabel(m.ts)}>
            {timeLabel(m.ts)}
          </span>
          {isInbound && (() => {
            // A live upload outranks an earlier attempt's error: the files
            // are moving now, so "Not sent" would be false.
            if (uploadProgress) {
              return <span aria-label="uploading" className="opacity-80">{uploadProgressLabel(uploadProgress)}</span>
            }
            if (isOptimistic) return <span aria-label="sending" className="opacity-80">Sending…</span>
            if (m.status === 'error') {
              return (
                <span className="text-red-200 font-medium" title={m.outbox_error ?? undefined}>
                  {retryingThisMessage
                    ? 'Retrying…'
                    : m.outbox_pending
                      ? `Not sent${m.outbox_error ? ` — ${m.outbox_error}` : ''} — tap to retry`
                      : 'Failed — tap to retry'}
                </span>
              )
            }
            if (isQueued) {
              if (m.outbox_pending) {
                return <span aria-label="waiting to send">Waiting to send…</span>
              }
              const queueNumber = Math.max(1, (m.queue_position ?? 0) + 1)
              const forcingThis = forceNow.isPending && forceNow.variables === m.id
              const forcedNext = m.force_dispatch || forcingThis
              const forceTitle = topicHasActiveTurn
                ? 'This message is first in line and will send when the current turn in this topic finishes.'
                : 'This message is first in line and will dispatch on the next worker tick.'
              return (
                <span aria-label="queued" className="inline-flex flex-wrap items-center gap-1.5">
                  <span>Queued #{queueNumber}</span>
                  {forcedNext
                    ? (
                      <span
                        title={forceTitle}
                        className={clsx(
                          'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md',
                          'bg-emerald-500/20 text-emerald-100',
                          'font-medium tracking-tight',
                        )}
                      >
                        <Zap className="w-3 h-3" />
                        {topicHasActiveTurn ? 'Next after current' : 'Next up'}
                      </span>
                    )
                    : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          forceNow.mutate(m.id)
                        }}
                        disabled={forceNow.isPending}
                        title={(m.queue_position ?? 0) > 0
                          ? 'Send this message immediately - bumps it to the front of the queue.'
                          : 'Send this message as soon as the current turn can accept it.'}
                        className={clsx(
                          'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md',
                          'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 transition-colors',
                          'font-medium tracking-tight',
                          forceNow.isPending && 'opacity-60 cursor-wait',
                        )}
                      >
                        <Zap className="w-3 h-3" />
                        Force now
                      </button>
                    )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setQueuedEditText(m.body)
                    }}
                    disabled={editQueued.isPending}
                    title="Edit this queued message before it is sent."
                    className={clsx(
                      'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md',
                      'bg-blue-500/20 text-blue-100 hover:bg-blue-500/30 transition-colors',
                      'font-medium tracking-tight',
                      editQueued.isPending && 'opacity-60 cursor-wait',
                    )}
                  >
                    <Pencil className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      void cancelQueuedMessage()
                    }}
                    disabled={cancelQueued.isPending}
                    title="Cancel this queued message and put it back in the composer."
                    className={clsx(
                      'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md',
                      'bg-red-500/20 text-red-100 hover:bg-red-500/30 transition-colors',
                      'font-medium tracking-tight',
                      cancelQueued.isPending && 'opacity-60 cursor-wait',
                    )}
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                </span>
              )
            }
            if (m.status === 'dispatched') {
              return <span aria-label="working">Working…</span>
            }
            return <span aria-label="sent">✓</span>
          })()}
        </div>
      </div>

      {!isInbound && actionsVisible && !isOptimistic && (
        <MessageActions
          className="agentchat-message-actions-touch"
          onCopy={copyText}
          copied={copied}
          onFork={() => onFork(m)}
          onReadAloud={toggleReadAloud}
          canReadAloud={canReadAloud}
          reading={readingThisMessage}
          align="right"
        />
      )}
      {!isInbound && !isOptimistic && (
        <MessageActions
          className="agentchat-message-actions-desktop-reserved"
          onCopy={copyText}
          copied={copied}
          onFork={() => onFork(m)}
          onReadAloud={toggleReadAloud}
          canReadAloud={canReadAloud}
          reading={readingThisMessage}
          align="right"
        />
      )}
    </div>
  )
})

function MessageActions({
  onCopy, copied, onFork, onReadAloud, canReadAloud, reading, align = 'left', className,
}: {
  onCopy: () => void
  copied: boolean
  onFork: () => void
  onReadAloud: () => void
  canReadAloud: boolean
  reading: boolean
  align?: 'left' | 'right'
  className?: string
}) {
  return (
    <div
      className={clsx(
        'flex items-center gap-0.5 self-center mx-1.5',
        align === 'left' && 'order-first',
        className,
      )}
    >
      {canReadAloud && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onReadAloud()
          }}
          title={reading ? 'Stop read aloud' : 'Read aloud'}
          aria-label={reading ? 'Stop read aloud' : 'Read aloud'}
          className={clsx(
            'grid place-items-center w-7 h-7 rounded-lg transition-colors',
            reading
              ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-950/50'
              : 'text-stone-400 hover:text-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800',
          )}
        >
          {reading ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onCopy()
        }}
        title={copied ? 'Copied' : 'Copy text'}
        aria-label="Copy"
        className={clsx(
          'grid place-items-center w-7 h-7 rounded-lg transition-colors',
          'text-stone-400 hover:text-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800',
        )}
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onFork()
        }}
        title="Fork chat"
        aria-label="Fork chat"
        className={clsx(
          'grid place-items-center w-7 h-7 rounded-lg transition-colors',
          'text-stone-400 hover:text-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800',
        )}
      >
        <GitBranch className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function ForkMessageDialog({
  mode,
  freeChats,
  topicTitle,
  setTopicTitle,
  pending,
  error,
  onCancel,
  onForkToChat,
  onForkToTopic,
}: {
  mode: 'chat' | 'topic'
  freeChats: Chat[]
  topicTitle: string
  setTopicTitle: (value: string) => void
  pending: boolean
  error: string | null
  onCancel: () => void
  onForkToChat: (chatId: string) => void
  onForkToTopic: () => void
}) {
  const trimmedTitle = topicTitle.trim()

  function submitTopic(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!trimmedTitle || pending) return
    onForkToTopic()
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-stone-950/50 p-3 sm:items-center"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !pending) onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="fork-message-title"
        aria-busy={pending}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl border border-stone-200 bg-white p-3 shadow-2xl dark:border-stone-700 dark:bg-stone-900"
      >
        <div className="flex items-center gap-2">
          <h2 id="fork-message-title" className="min-w-0 flex-1 text-sm font-semibold text-stone-900 dark:text-stone-100">
            Fork chat
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="grid size-8 place-items-center rounded-lg text-stone-500 transition-colors hover:bg-stone-100 disabled:opacity-50 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        <p className="mt-2 text-xs text-stone-600 dark:text-stone-300">
          Fork through this message. Verified Codex replies can retain native context;
          other forks copy visible history. Codex cache use is reported when available.
        </p>
        {mode === 'topic' ? (
          <form onSubmit={submitTopic}>
            <label htmlFor="fork-topic-title" className="mt-2 block text-xs font-medium text-stone-600 dark:text-stone-300">
              Topic name
            </label>
            <input
              id="fork-topic-title"
              value={topicTitle}
              onChange={(e) => setTopicTitle(e.target.value)}
              disabled={pending}
              autoFocus
              className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 dark:border-stone-700 dark:bg-stone-950 dark:text-stone-100"
            />
            {error && (
              <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200">
                {error}
              </p>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={pending}
                className="rounded-lg px-3 py-2 text-sm text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-50 dark:text-stone-300 dark:hover:bg-stone-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!trimmedTitle || pending}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-stone-300 dark:disabled:bg-stone-700"
              >
                <GitBranch className="size-4" />
                {pending ? 'Forking' : 'Fork'}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-2">
            {freeChats.length === 0 ? (
              <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                No free lanes right now.
              </p>
            ) : (
              <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-stone-200 dark:border-stone-700">
                {freeChats.map((chat) => (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => onForkToChat(chat.id)}
                    disabled={pending}
                    className="flex w-full items-center gap-2 border-b border-stone-100 px-2.5 py-2 text-left transition-colors last:border-b-0 hover:bg-stone-50 disabled:opacity-60 dark:border-stone-800 dark:hover:bg-stone-800"
                  >
                    <AgentBadge
                      agent={chat.uses_multiple_agents ? 'mixed' : (chat.display_agent || chat.preferred_model || chat.agent)}
                      size={28}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-stone-900 dark:text-stone-100">
                      {chat.display_name || chat.id}
                    </span>
                    <GitBranch className="size-4 text-stone-400" />
                  </button>
                ))}
              </div>
            )}
            {error && (
              <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Inline image attachment. Renders the actual image inside the bubble
 * (capped to ~280×400 so a 4MP phone photo doesn't blow out the chat
 * column). Tap opens a full-screen in-app viewer. Mark's audit:
 * "attaching documents is terrible. It shows text rather than the
 * image." Now images show as images, the way every chat app since
 * 2010 has done it.
 *
 * Optimistic-send messages reference a `blob:` URL so the image flashes
 * instantly while the upload + server roundtrip resolve. That URL gets
 * revoked ~2s after send (memory hygiene), or when the Composer unmounts
 * (e.g. user navigates away and back). If the cache hasn't yet replaced
 * the optimistic bubble with the server-fetched one — slow refetch,
 * navigation, etc. — the bubble would otherwise show Chrome's broken-
 * image icon + alt text. We listen for that error and fall back to the
 * canonical `/api/attachments/<id>/file` URL, which is always durable
 * once POST /messages has committed the attachment row (well before
 * the blob revoke timer fires). */
function AttachmentImage({ att, onOpen }: { att: Attachment; onOpen: (att: Attachment) => void }) {
  const apiUrl = `/api/attachments/${att.id}/file`
  const [src, setSrc] = useState(att.url)
  const triedFallback = useRef(false)
  // Resync if the same component instance gets a new attachment URL
  // (e.g. optimistic→real swap when att.id stays equal across refetch).
  useEffect(() => {
    setSrc(att.url)
    triedFallback.current = false
  }, [att.url])
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onOpen(att)
      }}
      className="block rounded-xl overflow-hidden max-w-[280px] max-h-[400px] border border-stone-200/40 dark:border-stone-700/60 shadow-sm bg-stone-100 dark:bg-stone-900 active:scale-[0.99] transition"
      title={att.filename || 'image'}
      aria-label="Open image full screen"
    >
      <img
        src={src}
        alt={att.filename || 'attachment'}
        loading="lazy"
        decoding="async"
        className="block w-full h-auto"
        onError={() => {
          if (triedFallback.current || src === apiUrl) return
          triedFallback.current = true
          setSrc(apiUrl)
        }}
      />
    </button>
  )
}

function MessageAttachments({
  attachments, isInbound, onOpenImage,
}: {
  attachments: Attachment[]
  isInbound: boolean
  onOpenImage: (att: Attachment) => void
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {attachments.map((att) => {
        if (att.kind === 'image') return <AttachmentImage key={att.id} att={att} onOpen={onOpenImage} />
        if (att.kind === 'video') return <AttachmentVideo key={att.id} att={att} />
        if (att.kind === 'audio' || att.kind === 'voice') return <AttachmentAudio key={att.id} att={att} />
        return <AttachmentChip key={att.id} att={att} isInbound={isInbound} />
      })}
    </div>
  )
}

function AttachmentDownloadLink({
  att, className, children,
}: { att: Attachment; className?: string; children: ReactNode }) {
  const [error, setError] = useState<string | null>(null)
  const [opening, setOpening] = useState(false)
  async function download(e: MouseEvent<HTMLAnchorElement>) {
    e.stopPropagation()
    if (!isAgentChatNativeApp()) return
    e.preventDefault()
    if (opening) return
    setOpening(true)
    setError(null)
    try {
      const meta = await fetchAttachmentOpen(att.id)
      if (isNativeExternalOpenAvailable()) {
        await openNativeExternalUrl({ url: meta.fallback_url, fallbackUrl: meta.fallback_url })
      } else {
        window.location.assign(meta.fallback_url)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed. Please retry.')
    } finally {
      setOpening(false)
    }
  }
  return (
    <>
      <a href={attachmentDownloadHref(att.id)} target="_blank" rel="noreferrer"
        onClick={download} className={className} aria-label="Download attachment"
        title={error || 'Download'} aria-busy={opening}>
        {children}
      </a>
      {error && <span role="alert" className="text-xs text-red-600">{error}</span>}
    </>
  )
}

function ImageAttachmentViewer({
  att,
  onClose,
}: {
  att: Attachment
  onClose: () => void
}) {
  const apiUrl = `/api/attachments/${att.id}/file`
  const [src, setSrc] = useState(att.url)
  const triedFallback = useRef(false)

  useEffect(() => {
    setSrc(att.url)
    triedFallback.current = false
  }, [att.url])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-stone-950/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={att.filename || 'Image attachment'}
      onClick={onClose}
    >
      <header className="flex items-center gap-2 px-3 py-3 border-b border-stone-800/60 bg-stone-950 text-stone-100 shrink-0">
        <ImageIcon className="w-4 h-4 text-stone-400" aria-hidden />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">
          {att.filename || 'Image'}
        </h2>
        <a
          href={apiUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="grid h-10 w-10 place-items-center rounded-full text-stone-300 transition hover:bg-stone-800 active:scale-95"
          aria-label="Open original image"
          title="Open original"
        >
          <ExternalLink className="h-5 w-5" />
        </a>
        <AttachmentDownloadLink
          att={att}
          className="grid h-10 w-10 place-items-center rounded-full text-stone-300 transition hover:bg-stone-800 active:scale-95"
        >
          <Download className="h-5 w-5" />
        </AttachmentDownloadLink>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          className="grid h-10 w-10 place-items-center rounded-full text-stone-300 transition hover:bg-stone-800 active:scale-95"
          aria-label="Close image viewer"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 items-center justify-center p-2 sm:p-4">
        <img
          src={src}
          alt={att.filename || 'attachment'}
          decoding="async"
          className="max-h-full max-w-full select-none object-contain"
          onClick={(e) => e.stopPropagation()}
          onError={() => {
            if (triedFallback.current || src === apiUrl) return
            triedFallback.current = true
            setSrc(apiUrl)
          }}
        />
      </div>
    </div>
  )
}

function playbackAttachmentUrl(att: Attachment): string {
  if (att.url.startsWith('blob:') || att.url.startsWith('data:')) return att.url
  const version = att.size_bytes ?? att.id
  const separator = att.url.includes('?') ? '&' : '?'
  return `${att.url}${separator}v=${encodeURIComponent(String(version))}`
}


/** Inline video player. Browser-native controls, lazy metadata-only
 * preload so the bubble appears instantly without buffering the file. */
function AttachmentVideo({ att }: { att: Attachment }) {
  const src = playbackAttachmentUrl(att)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  return (
    <div className="space-y-2 max-w-[320px]">
      <video
        src={src}
        controls
        preload="metadata"
        playsInline
        onError={() => setFailedSrc(src)}
        onLoadedData={() => setFailedSrc(null)}
        className="block max-w-full max-h-[400px] rounded-xl bg-black"
      />
      {failedSrc === src && <p role="status" className="text-xs">Video could not play here. Open or download it below.</p>}
      {!att.url.startsWith('blob:') && !att.url.startsWith('data:') && (
        <AttachmentChip att={att} isInbound={false} label="Open or download video" />
      )}
    </div>
  )
}


/** Inline audio player — voice notes + attached audio files. */
function AttachmentAudio({ att }: { att: Attachment }) {
  const src = playbackAttachmentUrl(att)
  return (
    <audio
      src={src}
      controls
      preload="metadata"
      className="block w-full max-w-[320px]"
    >
      <a href={src} target="_blank" rel="noreferrer">
        {att.filename || 'audio'}
      </a>
    </audio>
  )
}


/** Inline chip for non-media attachments (PDFs, docs).
 *
 * Two platforms, two flows:
 *
 * **Android Chrome PWA**: the <a href> is the pre-fetched intent:// URL.
 * A tap is a direct user-gesture click on intent://, which Chrome
 * dispatches to Android's intent resolver.
 *
 * **Capacitor Android app**: the chip calls AgentChatExternalOpen from
 * the tap handler. Android WebView does not reliably dispatch Chrome's
 * intent:// URLs or render PDFs inline, so native code opens the signed
 * file URL with ACTION_VIEW. Android then applies the user's default
 * app choice when one exists, or shows its normal resolver when there
 * are multiple handlers. If the APK is older and lacks that plugin, the
 * click navigates to a tokenized public launch URL. That host is outside
 * the app WebView's allowNavigation list, so Capacitor hands it to Chrome,
 * and Chrome shows Android's resolver instead of downloading inside
 * AgentChat.
 *
 * **Desktop** (laptop browser): intent:// URLs do nothing on desktop —
 * Chrome/Firefox/Safari refuse to navigate to an unknown scheme. So the
 * chip points at the file URL with ?disposition=attachment so a tap
 * downloads the file directly. Browsers' built-in PDF viewers can still
 * open the downloaded file with one extra click; Mark wanted reliable
 * download on laptop, not inline preview.
 *
 * v18 tried the intent flow via a JS-redirected HTML wrapper but Chrome
 * refuses to dispatch intent:// without a direct gesture on the link
 * itself (CCT-loaded JS doesn't qualify). v19 made the href the intent
 * URL directly — which fixed Android but silently broke laptop, since
 * intent:// is a no-op there. v20 splits the two paths at the chip. */
function AttachmentChip({ att, isInbound, label }: { att: Attachment; isInbound: boolean; label?: string }) {
  const Icon =
    att.kind === 'pdf' || att.kind === 'document' ? FileText :
    att.kind === 'image' ? ImageIcon :
    att.kind === 'video' ? VideoIcon :
    att.kind === 'audio' || att.kind === 'voice' ? FileAudio :
    FileIcon
  const sizeKb = att.size_bytes != null ? Math.round(att.size_bytes / 1024) : null
  const isAndroid =
    typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)
  const isNativeApp = isAgentChatNativeApp()
  const hasNativeOpen = isNativeExternalOpenAvailable()
  const [nativeOpening, setNativeOpening] = useState(false)
  const [nativeError, setNativeError] = useState<string | null>(null)
  const openUrl = attachmentOpenPath(att.id)
  const { data: openMeta, dataUpdatedAt } = useQuery({
    queryKey: ['attachment-intent', att.id],
    queryFn: () => fetchAttachmentOpen(att.id),
    enabled: isAndroid && !isNativeApp,
    staleTime: 3 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  })
  const freshIntent = openMeta && isAttachmentOpenFresh(dataUpdatedAt)
  const href = isNativeApp ? openUrl
    : isAndroid ? (freshIntent ? openMeta.intent_url : openUrl)
    : attachmentDownloadHref(att.id)

  async function onNativeOpen(e: MouseEvent<HTMLAnchorElement>) {
    if (!isNativeApp) {
      // A suspended phone can retain this anchor long after its signed URL
      // expires. Authenticate a new launch instead of following that URL.
      if (isAndroid && !isAttachmentOpenFresh(dataUpdatedAt)) {
        e.preventDefault()
        window.location.assign(openUrl)
      }
      return
    }
    e.preventDefault()
    if (nativeOpening) return
    setNativeOpening(true)
    setNativeError(null)
    try {
      const meta = await fetchAttachmentOpen(att.id)
      if (hasNativeOpen) {
        await openNativeExternalUrl({
          url: meta.file_url,
          fallbackUrl: meta.external_open_url,
          mimeType: meta.mime_type,
          filename: meta.filename || att.filename,
        })
      } else {
        window.location.assign(meta.external_open_url)
      }
    } catch (err) {
      setNativeError(err instanceof Error ? err.message : 'Could not open this attachment. Please retry.')
    } finally {
      setNativeOpening(false)
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
    <a
      href={href}
      // Desktop: open in new tab so the chat page stays put. The signed
      // token URL has disposition=attachment so Chrome triggers a download
      // instead of rendering the PDF inline in the new tab.
      // Android Chrome PWA: no target; intent:// fires the OS chooser.
      // Native app: no target; click handler opens native bridge or
      // external public launch URL.
      target={isAndroid || isNativeApp ? undefined : '_blank'}
      rel="noreferrer"
      onClick={onNativeOpen}
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs',
        'border transition-colors max-w-[280px]',
        isInbound
          ? 'bg-blue-700/30 border-blue-300/40 text-blue-50 hover:bg-blue-700/40'
          : 'bg-stone-100 dark:bg-stone-700/60 border-stone-300 dark:border-stone-600 ' +
            'text-stone-700 dark:text-stone-200 hover:bg-stone-200 dark:hover:bg-stone-700',
      )}
      title={nativeError || att.filename || 'attachment'}
      aria-busy={nativeOpening}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate font-medium">
        {nativeOpening ? 'Opening…' : (label || att.filename || 'file')}
      </span>
      {sizeKb != null && (
        <span className="text-[10px] opacity-70 shrink-0">{sizeKb} KB</span>
      )}
    </a>
    <AttachmentDownloadLink att={att}
      className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-300">
      <Download className="h-4 w-4" />
    </AttachmentDownloadLink>
    {nativeError && <span role="alert" className="text-xs text-red-600">{nativeError}</span>}
    </span>
  )
}


export default function MessageList({
  chatId,
  topicId,
  targetMessageId,
  aroundMessageId = targetMessageId,
  notificationOpen = false,
  notificationTargetMessageId = null,
  chatType = 'user-driven',
  currentTopicTitle = null,
  existingTopicTitles = [],
}: {
  chatId: string
  topicId?: string | null
  targetMessageId?: string | null
  aroundMessageId?: string | null
  notificationOpen?: boolean
  notificationTargetMessageId?: string | null
  chatType?: Chat['type']
  currentTopicTitle?: string | null
  existingTopicTitles?: Array<string | null | undefined>
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const openScope = `${chatId}:${topicId ?? ''}:${targetMessageId ?? 'default'}:${aroundMessageId ?? 'latest'}`
  const shouldResumeAtLatestInbound = !targetMessageId && !aroundMessageId
  // Perf-trace anchor — the route-enter mark fires the moment a new
  // (chatId, topicId) lands in this component so the matching
  // first-message-painted mark below has something to diff against.
  // Cheap: `performance.mark` is a no-op when the buffer is full and is
  // never called outside topic switches.
  const routeMarkRef = useRef('')
  if (typeof performance !== 'undefined' && routeMarkRef.current !== openScope) {
    routeMarkRef.current = openScope
    try { performance.mark(`topic-route-enter:${openScope}`) } catch { /* perf API can be disabled */ }
  }
  const [expandedHistoryScope, setExpandedHistoryScope] = useState<string | null>(null)
  const latestWindowOnly = shouldResumeAtLatestInbound && expandedHistoryScope !== openScope
  useEffect(() => {
    clearExpandedMessageReadModel(chatId, topicId ?? null)
    // Register the mounted scope immediately. List/startup warmers may finish
    // while the live query is still placeholder data; they must merge into the
    // visible transcript instead of replacing its cache-first window.
    // A notification/search target uses an older bounded window. A concurrent
    // latest-inbound warmer must merge its tail into that window, not replace
    // the exact message Mark opened (including in the offline first-paint store).
    if (shouldResumeAtLatestInbound || targetMessageId) {
      retainOpenMessageReadModel(chatId, topicId ?? null)
    }
    setExpandedHistoryScope(null)
    return () => clearExpandedMessageReadModel(chatId, topicId ?? null)
  }, [chatId, openScope, shouldResumeAtLatestInbound, targetMessageId, topicId])
  const { data: rawData, isLoading, error, isPlaceholderData } = useMessages(
    chatId,
    topicId ?? null,
    {
      aroundMessageId: aroundMessageId ?? null,
      notificationOpen,
      notificationTargetMessageId,
      latestWindowOnly,
    },
  )
  // P4 — Welcome-flash guard. Without this flag the "👋 New conversation"
  // empty state can render on a topic that DOES have messages, in two
  // narrow but real windows:
  //   (a) A previous topic load left an empty array in the messages cache
  //       (qc.getQueryData → []), so isLoading is false on first render
  //       even though we haven't fetched THIS topic yet.
  //   (b) Sub-renders during the topic switch — Virtuoso remount, key
  //       change — that fire while useMessages is mid-flight.
  // We track per-openScope whether the loader has produced any data (cache
  // hit or server response) at least once. Welcome renders ONLY when the
  // loader has resolved and the result is genuinely empty.
  const loaderResolvedScope = useRef<{ scope: string; resolved: boolean }>({ scope: '', resolved: false })
  if (loaderResolvedScope.current.scope !== openScope) {
    loaderResolvedScope.current = { scope: openScope, resolved: false }
  }
  if (rawData !== undefined && !loaderResolvedScope.current.resolved) {
    loaderResolvedScope.current.resolved = true
  }
  const loaderResolved = loaderResolvedScope.current.resolved
  // Topic views are filtered server-side before the message limit is applied.
  // Keep this client-side guard for persisted cache entries while the server
  // refetch catches up.
  const data = useMemo(() => {
    const inTopic = topicId
      ? (rawData ?? []).filter((m) => m.topic_id === topicId)
      : rawData
    if (!inTopic) return inTopic
    return inTopic.filter(messageVisibleInConversation)
  }, [rawData, topicId])
  const { data: chatRow } = useChat(chatId)
  const targetsPreResetMessage = data?.some((message) => (
    message.id === targetMessageId && message.ts <= (chatRow?.last_reset_at ?? 0)
  ))
  const laneResetAt = !topicId && !aroundMessageId && !targetsPreResetMessage ? (chatRow?.last_reset_at ?? 0) : 0
  const resetHistoryKey = `${openScope}:${laneResetAt}`
  const [openedPreResetHistory, setOpenedPreResetHistory] = useState<string | null>(null)
  const includePreResetHistory = openedPreResetHistory === resetHistoryKey
  useEffect(() => { setOpenedPreResetHistory(null) }, [openScope, laneResetAt])
  const msgs = useMemo(() => historyAfterReset(data ?? [], laneResetAt, includePreResetHistory),
    [data, laneResetAt, includePreResetHistory])
  const hasHiddenPreResetMessages = (data?.length ?? 0) > msgs.length
  // Every ordinary open lands at the very bottom (the latest message) and stays
  // there. There is deliberately no "resume at Mark's last inbound" anchoring —
  // that mid-list logic is what produced the "opens near the top / hops" bugs.
  // Only explicit search / deep-link opens (targetMessageId / aroundMessageId)
  // scroll somewhere other than the bottom.
  const shouldBottomAnchor = shouldResumeAtLatestInbound
  // Virtuoso owns the scroll container. We hold:
  //   - virtuosoRef: imperative scrollToIndex for the keyboard handler
  //   - scrollerRef: handle on Virtuoso's internal scroll element so the
  //     elastic-overscroll hook can attach touch listeners to the actual
  //     scrolling div (not our component root)
  //   - wasAtBottomRef: mirrors atBottomStateChange so the keyboard
  //     re-anchor only fires when the user was already pinned to bottom.
  //     Targeted opens (deep-link / search) start false until Virtuoso
  //     reports bottom; otherwise Android keyboard close can override.
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const scrollerRef = useRef<HTMLElement | null>(null)
  const wasAtBottomRef = useRef(shouldBottomAnchor)
  const messageTextSelectionPauseUntilRef = useRef(0)
  const openGenerationRef = useRef(0)
  const [bottomAnchorReadyState, setBottomAnchorReadyState] = useState<{
    scope: string
    generation: number
  }>({ scope: '', generation: -1 })
  const [userReleasedBottomAnchor, setUserReleasedBottomAnchor] = useState<{
    scope: string
    released: boolean
  }>({ scope: '', released: false })
  const atBottomScopeRef = useRef('')
  if (atBottomScopeRef.current !== openScope) {
    atBottomScopeRef.current = openScope
    openGenerationRef.current += 1
    wasAtBottomRef.current = shouldBottomAnchor
    messageTextSelectionPauseUntilRef.current = 0
  }
  const openGeneration = openGenerationRef.current
  // WhatsApp-style elastic stretch when pulling past the top / bottom of
  // the messages list (or in any direction if the list isn't scrollable).
  // Hook iterates scrollerRef.current.children — with Virtuoso those
  // children are the per-item wrappers, which is exactly what we want
  // stretched.
  useElasticOverscroll(scrollerRef, {
    ignoreSelector: '.agentchat-selectable-message-text',
  })

  const messageTextSelectionIsActive = useCallback(() => (
    Date.now() < messageTextSelectionPauseUntilRef.current ||
    hasActiveMessageTextSelection(scrollerRef.current)
  ), [])

  const pauseAutoScrollForMessageTextSelection = useCallback(() => {
    messageTextSelectionPauseUntilRef.current =
      Date.now() + MESSAGE_TEXT_SELECTION_AUTO_SCROLL_PAUSE_MS
    wasAtBottomRef.current = false
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const onSelectionChange = () => {
      if (hasActiveMessageTextSelection(scrollerRef.current)) {
        pauseAutoScrollForMessageTextSelection()
      }
    }
    document.addEventListener('selectionchange', onSelectionChange)
    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, [pauseAutoScrollForMessageTextSelection])

  const scrollToLatest = useCallback(() => {
    const el = scrollerRef.current
    virtuosoRef.current?.scrollToIndex({
      index: 'LAST',
      align: 'end',
      behavior: 'auto',
    })
    virtuosoRef.current?.autoscrollToBottom()
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' })
    }
  }, [])

  // P4 perf trace — pair the topic-route-enter mark above with a
  // first-message-painted mark the first time the message list is
  // non-empty for a given openScope. The delta is the cache-first
  // render time (~milliseconds when cache hits) and is visible in the
  // browser perf tab + dev-mode console for headless verification.
  const firstPaintMarkRef = useRef('')
  useEffect(() => {
    if (typeof performance === 'undefined') return
    if (msgs.length === 0) return
    if (firstPaintMarkRef.current === openScope) return
    firstPaintMarkRef.current = openScope
    try {
      performance.mark(`first-message-painted:${openScope}`)
      performance.measure(
        `topic-open:${openScope}`,
        `topic-route-enter:${openScope}`,
        `first-message-painted:${openScope}`,
      )
      if (import.meta.env.DEV) {
        const entries = performance.getEntriesByName(`topic-open:${openScope}`)
        const last = entries[entries.length - 1]
        if (last) {
          // eslint-disable-next-line no-console
          console.debug(`[perf] topic-open ${openScope}: ${last.duration.toFixed(1)}ms`)
        }
      }
    } catch {
      /* perf API can be disabled or buffer full */
    }
  }, [msgs.length, openScope])
  const notificationRenderedScopeRef = useRef('')
  useEffect(() => {
    if (!notificationOpen) return
    if (isPlaceholderData) return
    if (msgs.length === 0) return
    const latest = msgs[msgs.length - 1]
    const scope = `${openScope}:${msgs.length}:${latest?.id ?? ''}`
    if (notificationRenderedScopeRef.current === scope) return
    const targetMessagePresent = notificationTargetMessageId
      ? msgs.some((message) => message.id === notificationTargetMessageId)
      : null
    if (notificationTargetMessageId && !targetMessagePresent) return
    notificationRenderedScopeRef.current = scope
    finishNotificationOpenTiming({
      chatId,
      topicId: topicId ?? null,
      messageId: notificationTargetMessageId,
      latestMessageId: latest?.id ?? null,
      messageCount: msgs.length,
      targetMessagePresent,
      notificationOpen,
      appVersion: __BUILD_VERSION__,
    })
  }, [chatId, isPlaceholderData, msgs, notificationOpen, notificationTargetMessageId, openScope, topicId])
  const [openImage, setOpenImage] = useState<Attachment | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const historyAnchorRef = useRef<{ id: string; top: number } | null>(null)
  const historyRestoringRef = useRef(false)
  const historyRequestRef = useRef({ scope: openScope, pending: false, cursor: '' })
  if (historyRequestRef.current.scope !== openScope) {
    historyRequestRef.current = { scope: openScope, pending: false, cursor: '' }
  }
  useEffect(() => {
    const request = historyRequestRef.current
    historyAnchorRef.current = null
    historyRestoringRef.current = false
    setHistoryLoading(false)
    setHistoryError(null)
    setHistoryRevealMessageKey(null)
    return () => {
      if (historyRequestRef.current === request) {
        historyRequestRef.current = { scope: '', pending: false, cursor: '' }
      }
      historyAnchorRef.current = null
    }
  }, [openScope])
  const [historyRevealMessageKey, setHistoryRevealMessageKey] = useState<string | null>(null)
  // Derive in-flight gates for the tool-progress chip from server truth:
  //   - latestAgentId: only the freshest agent bubble can ever show the
  //     chip; older bubbles never light up again, even if their cache
  //     slot was orphaned by a missed agent_reply.
  //   - laneIsBusy: when the chat's lane is not actively running, the
  //     chip is hidden no matter what the cache slot says.
  //
  // These hooks must stay above every early return. Otherwise a chat that
  // renders the skeleton first and the messages later changes hook order
  // and React blanks the pane with error #310.
  const latestAgentId = useMemo(() => {
    for (let i = msgs.length - 1; i >= 0; i--) {
      const cur = msgs[i]
      if (cur.direction === 'outbound' || cur.direction === 'agent') return cur.id
    }
    return null
  }, [msgs])
  // Index of the newest question card: an unanswered card above it has been
  // replaced, so Mark never answers an old or duplicated question.
  const latestCardIndex = useMemo(() => latestQuestionCardIndex(msgs.map((cur) => cur.body)), [msgs])
  const answeredQuestionIds = useMemo(() => new Set(msgs.flatMap((message, index) => (
    message.body?.includes('__ASK_USER_QUESTION__') && isQuestionCardAnswered(msgs, index)
      ? [message.id] : []
  ))), [msgs])
  const messageIndexById = useMemo(
    () => new Map(msgs.map((cur, i) => [cur.id, i] as const)),
    [msgs],
  )
  const forkMessage = useForkMessage(chatId)
  const [forkingMessage, setForkingMessage] = useState<Message | null>(null)
  const [forkTopicTitle, setForkTopicTitle] = useState('')
  const [forkError, setForkError] = useState<string | null>(null)
  const { data: allChats } = useChats({ enabled: forkingMessage !== null, poll: false })
  const laneIsBusy = chatRow?.lane_status === 'busy'
  const forkMode: 'chat' | 'topic' = chatType === 'auto-spawn' && topicId ? 'topic' : 'chat'
  const freeForkChats = useMemo(() => (
    forkableLaneTargets(allChats, chatId)
  ), [allChats, chatId])
  const openForkDialog = useCallback((message: Message) => {
    setForkingMessage(message)
    setForkError(null)
    setForkTopicTitle(nextForkTopicTitle(currentTopicTitle, existingTopicTitles))
  }, [currentTopicTitle, existingTopicTitles])
  const closeForkDialog = useCallback(() => {
    if (forkMessage.isPending) return
    setForkingMessage(null)
    setForkError(null)
  }, [forkMessage.isPending])
  const finishFork = useCallback((targetChatId: string, targetTopicId: string | null) => {
    setForkingMessage(null)
    setForkError(null)
    navigate(chatTarget(targetChatId, targetTopicId))
  }, [navigate])
  const forkToChat = useCallback(async (targetChatId: string) => {
    if (!forkingMessage || forkMessage.isPending) return
    try {
      const result = await forkMessage.mutateAsync({
        message_id: forkingMessage.id,
        target_chat_id: targetChatId,
      })
      finishFork(result.target_chat_id, result.target_topic_id)
    } catch (err) {
      setForkError(err instanceof Error ? err.message : 'Fork failed')
    }
  }, [finishFork, forkMessage, forkingMessage])
  const forkToTopic = useCallback(async () => {
    if (!forkingMessage || forkMessage.isPending) return
    const title = forkTopicTitle.trim()
    if (!title) return
    try {
      const result = await forkMessage.mutateAsync({
        message_id: forkingMessage.id,
        target_topic_title: title,
      })
      finishFork(result.target_chat_id, result.target_topic_id)
    } catch (err) {
      setForkError(err instanceof Error ? err.message : 'Fork failed')
    }
  }, [finishFork, forkMessage, forkTopicTitle, forkingMessage])
  const latestLocalAgentReplyAt = useMemo(() => {
    const all = rawData ?? []
    for (let i = all.length - 1; i >= 0; i--) {
      const cur = all[i]
      if (
        (cur.direction === 'outbound' || cur.direction === 'agent') &&
        cur.status === 'received'
      ) {
        return cur.ts
      }
    }
    return 0
  }, [rawData])
  const hasLocalActiveInbound = useMemo(() => {
    return (rawData ?? []).some((m) => (
      m.direction === 'inbound' &&
      (m.status === 'queued' || m.status === 'dispatched')
    ))
  }, [rawData])

  useEffect(() => {
    if (!rawData || !chatRow) return
    const serverHasNoActiveWork =
      (chatRow.queue_depth ?? 0) === 0 &&
      (chatRow.active_work_depth ?? 0) === 0
    const serverHasNewerReply =
      (chatRow.last_agent_reply_at ?? 0) > latestLocalAgentReplyAt

    if ((serverHasNoActiveWork && hasLocalActiveInbound) || serverHasNewerReply) {
      preloadMessageReadModel(qc, chatId, topicId ?? null, MESSAGES_INITIAL_FETCH_LIMIT, {
        latestWindowOnly: true,
      })
    }
  }, [
    chatRow,
    chatId,
    hasLocalActiveInbound,
    latestLocalAgentReplyAt,
    qc,
    rawData,
    topicId,
  ])

  const items = useMemo(() => buildListItems(msgs), [msgs])
  const automaticHistory = !aroundMessageId && !hasHiddenPreResetMessages && !includePreResetHistory
  const [historyIndex, setHistoryIndex] = useState({
    scope: openScope, items, firstItemIndex: HISTORY_FIRST_ITEM_INDEX,
  })
  let firstItemIndex = historyIndex.firstItemIndex
  if (historyIndex.scope !== openScope) {
    firstItemIndex = HISTORY_FIRST_ITEM_INDEX
    setHistoryIndex({ scope: openScope, items, firstItemIndex })
  } else if (historyIndex.items !== items) {
    const previousMessageIndex = historyIndex.items.findIndex((item) => item.kind === 'message')
    const previousMessage = historyIndex.items[previousMessageIndex]
    const nextMessageIndex = previousMessage?.kind === 'message'
      ? items.findIndex((item) => item.kind === 'message' && messageStableKey(item.m) === messageStableKey(previousMessage.m))
      : -1
    firstItemIndex = prependFirstItemIndex(firstItemIndex, previousMessageIndex, nextMessageIndex)
    setHistoryIndex({ scope: openScope, items, firstItemIndex })
  }
  if (aroundMessageId) firstItemIndex = 0
  const messageSetAnchor = useMemo(() => {
    const first = msgs[0]
    const latest = msgs[msgs.length - 1]
    return [
      openScope,
      items.length,
      first ? messageStableKey(first) : 'empty',
      latest ? messageStableKey(latest) : 'empty',
    ].join(':')
  }, [items.length, msgs, openScope])
  const bottomAnchorReleased =
    userReleasedBottomAnchor.scope === openScope && userReleasedBottomAnchor.released
  const bottomAnchorReady =
    !shouldBottomAnchor ||
    bottomAnchorReleased ||
    (
      bottomAnchorReadyState.scope === openScope &&
      bottomAnchorReadyState.generation === openGeneration
    )
  const latestMessageAnchor = useMemo(() => {
    const latest = msgs[msgs.length - 1]
    if (!latest) return ''
    return [
      latest.client_id ?? latest.id,
      latest.ts,
      latest.status,
      latest.body.length,
      latest.attachments?.length ?? 0,
    ].join(':')
  }, [msgs])
  const activeTopicIds = useMemo(() => {
    const ids = new Set<string>()
    for (const msg of msgs) {
      if (msg.topic_id && msg.status === 'dispatched') ids.add(msg.topic_id)
    }
    return ids
  }, [msgs])
  // Only a targeted open suppresses expansion — it is already anchored mid-list
  // and pages both directions. Notification opens are ordinary bounded tails,
  // so they must offer Load older whenever the server says history exists.
  const canExpandHistory =
    !aroundMessageId &&
    (hasHiddenPreResetMessages || scopeHasOlderMessages(data ?? [], chatId, topicId ?? null))
  const expandHistory = useCallback(async () => {
    const request = historyRequestRef.current
    if (!canExpandHistory || request.pending) return
    if (hasHiddenPreResetMessages) {
      const previousSessionMessage = data?.filter((message) => message.ts <= laneResetAt).at(-1)
      wasAtBottomRef.current = false
      setUserReleasedBottomAnchor({ scope: openScope, released: true })
      setOpenedPreResetHistory(resetHistoryKey)
      setExpandedHistoryScope(openScope)
      setHistoryRevealMessageKey(previousSessionMessage ? messageStableKey(previousSessionMessage) : null)
      return
    }
    const beforeMessageId = msgs[0]?.id
    if (!beforeMessageId) return
    request.pending = true
    request.cursor = beforeMessageId
    wasAtBottomRef.current = false
    setUserReleasedBottomAnchor({ scope: openScope, released: true })
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      const expanded = await expandMessageReadModel(qc, chatId, topicId ?? null, beforeMessageId, {
        requireCompleteArchive: laneResetAt > 0 && automaticHistory,
        isCurrent: () => historyRequestRef.current === request,
        beforeMerge: () => {
          const el = scrollerRef.current
          if (!automaticHistory || !el) return
          if (historyAnchorRef.current) return
          const top = el.getBoundingClientRect().top
          const row = Array.from(el.querySelectorAll<HTMLElement>('[data-agentchat-message-id]'))
            .find((candidate) => candidate.getBoundingClientRect().bottom > top)
          historyAnchorRef.current = row
            ? { id: row.dataset.agentchatMessageId!, top: row.getBoundingClientRect().top - top }
            : null
          historyRestoringRef.current = Boolean(row)
        },
      })
      if (historyRequestRef.current !== request) return
      const beforeIndex = expanded.findIndex((message) => message.id === beforeMessageId)
      let revealMessage: Message | null = null
      for (let index = beforeIndex - 1; index >= 0; index -= 1) {
        const candidate = expanded[index]
        if (candidate?.body.trim()) {
          revealMessage = candidate
          break
        }
      }
      wasAtBottomRef.current = false
      setUserReleasedBottomAnchor({ scope: openScope, released: true })
      if (!automaticHistory) setHistoryRevealMessageKey(revealMessage ? messageStableKey(revealMessage) : null)
      setExpandedHistoryScope(openScope)
    } catch {
      if (historyRequestRef.current === request) setHistoryError('Couldn’t load earlier messages.')
    } finally {
      request.pending = false
      if (historyRequestRef.current === request) setHistoryLoading(false)
    }
  }, [automaticHistory, canExpandHistory, chatId, data, hasHiddenPreResetMessages, laneResetAt, msgs, openScope, qc, resetHistoryKey, topicId])
  useLayoutEffect(() => {
    const anchor = historyAnchorRef.current
    const el = scrollerRef.current
    if (!anchor || !el) return
    // Virtuoso handles the prepend; correct only the residual measurement
    // difference from a moved day separator. New gestures cancel correction.
    let frame = 0
    let remaining = 4
    const settle = () => {
      if (--remaining > 0) {
        frame = requestAnimationFrame(settle)
        return
      }
      const row = Array.from(el.querySelectorAll<HTMLElement>('[data-agentchat-message-id]'))
        .find((candidate) => candidate.dataset.agentchatMessageId === anchor.id)
      if (row) el.scrollTop += row.getBoundingClientRect().top - el.getBoundingClientRect().top - anchor.top
      historyAnchorRef.current = null
      historyRestoringRef.current = false
    }
    frame = requestAnimationFrame(settle)
    const cancel = () => {
      cancelAnimationFrame(frame)
      historyAnchorRef.current = null
      historyRestoringRef.current = false
    }
    el.addEventListener('touchmove', cancel, { passive: true })
    el.addEventListener('wheel', cancel, { passive: true })
    el.addEventListener('keydown', cancel)
    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener('touchmove', cancel)
      el.removeEventListener('wheel', cancel)
      el.removeEventListener('keydown', cancel)
    }
  }, [items, openScope])
  useEffect(() => {
    const el = scrollerRef.current
    if (!automaticHistory || !el) return
    return watchUpwardHistoryScroll(el, () => {
      if (hasActiveMessageTextSelection(el)) return
      if (historyRequestRef.current.cursor === msgs[0]?.id) return
      void expandHistory()
    })
  }, [automaticHistory, expandHistory, msgs, openScope])
  const HistoryHeader = useCallback(() => {
    if (automaticHistory) {
      return (
        <div className="flex h-10 items-center justify-center text-xs text-stone-500" role="status">
          {historyError
            ? <button type="button" className="min-h-9 px-3 underline" onClick={() => { void expandHistory() }}>Couldn’t load earlier messages. Retry</button>
            : historyLoading ? 'Loading earlier messages…' : null}
        </div>
      )
    }
    if (!canExpandHistory) return null
    return (
      <div className="flex h-10 items-center justify-center px-3">
        <button
          type="button"
          onClick={() => { void expandHistory() }}
          disabled={historyLoading}
          className="inline-flex min-h-9 items-center gap-2 rounded-md border border-stone-200 bg-white px-3 text-sm font-medium text-stone-700 shadow-sm active:bg-stone-100 disabled:opacity-60 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-200"
        >
          <ChevronUp className="h-4 w-4" aria-hidden="true" />
          <span>{historyLoading ? 'Loading older...' : historyError ? 'Retry loading older' : 'Load older'}</span>
        </button>
      </div>
    )
  }, [automaticHistory, canExpandHistory, expandHistory, historyError, historyLoading])
  const targetItemIndex = useMemo(() => {
    if (!targetMessageId) return -1
    return items.findIndex((item) => item.kind === 'message' && item.m.id === targetMessageId)
  }, [items, targetMessageId])
  useLayoutEffect(() => {
    if (!historyRevealMessageKey) return
    const index = items.findIndex((item) => (
      item.kind === 'message' && messageStableKey(item.m) === historyRevealMessageKey
    ))
    if (index < 0) return
    let frame = requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({
        index,
        align: 'start',
        behavior: 'auto',
      })
      frame = requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({
          index,
          align: 'start',
          behavior: 'auto',
        })
        setHistoryRevealMessageKey(null)
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [historyRevealMessageKey, items])

  const scrollToBottom = useCallback(() => {
    if (!shouldBottomAnchor) return
    if (!wasAtBottomRef.current) return
    if (messageTextSelectionIsActive()) return
    scrollToLatest()
  }, [messageTextSelectionIsActive, scrollToLatest, shouldBottomAnchor])

  const releaseBottomAnchorIfUserScrolledAway = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight
    if (bottomGap > 64) {
      wasAtBottomRef.current = false
      setUserReleasedBottomAnchor({ scope: openScope, released: true })
    }
  }, [openScope])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    let frame = 0
    let userScrollIntent = false
    const markUserScrollIntent = (event: Event) => {
      if (isMessageTextSelectionStart(event)) {
        pauseAutoScrollForMessageTextSelection()
        return
      }
      if (event instanceof KeyboardEvent && !isMessageScrollerKeyboardScrollIntent(event)) return
      userScrollIntent = true
      wasAtBottomRef.current = false
      setUserReleasedBottomAnchor({ scope: openScope, released: true })
    }
    const onScroll = () => {
      if (!userScrollIntent) return
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        userScrollIntent = false
        releaseBottomAnchorIfUserScrolledAway()
      })
    }
    el.addEventListener('touchstart', markUserScrollIntent, { passive: true })
    el.addEventListener('pointerdown', markUserScrollIntent, { passive: true })
    el.addEventListener('wheel', markUserScrollIntent, { passive: true })
    el.addEventListener('keydown', markUserScrollIntent)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener('touchstart', markUserScrollIntent)
      el.removeEventListener('pointerdown', markUserScrollIntent)
      el.removeEventListener('wheel', markUserScrollIntent)
      el.removeEventListener('keydown', markUserScrollIntent)
      el.removeEventListener('scroll', onScroll)
    }
  }, [items.length, openScope, pauseAutoScrollForMessageTextSelection, releaseBottomAnchorIfUserScrolledAway])

  useLayoutEffect(() => {
    if (!shouldBottomAnchor) return
    if (bottomAnchorReady) return
    if (items.length === 0) return
    if (!wasAtBottomRef.current) return
    let frame = 0
    let attempts = 0
    let stableFrames = 0
    let lastScrollHeight = -1
    let lastClientHeight = -1
    let cancelled = false
    const settleBottom = () => {
      if (cancelled) return
      if (!wasAtBottomRef.current) return
      if (messageTextSelectionIsActive()) return
      scrollToLatest()
      const el = scrollerRef.current
      const bottomGap = el ? el.scrollHeight - el.scrollTop - el.clientHeight : 0
      const scrollHeight = el?.scrollHeight ?? 0
      const clientHeight = el?.clientHeight ?? 0
      const bottomPinned = bottomGap <= BOTTOM_ANCHOR_READY_GAP_PX
      const layoutStable =
        bottomPinned &&
        scrollHeight === lastScrollHeight &&
        clientHeight === lastClientHeight

      stableFrames = layoutStable ? stableFrames + 1 : 0
      lastScrollHeight = scrollHeight
      lastClientHeight = clientHeight
      attempts += 1
      if (
        stableFrames >= BOTTOM_ANCHOR_STABLE_FRAMES ||
        (bottomPinned && attempts >= BOTTOM_ANCHOR_MAX_ATTEMPTS)
      ) {
        setBottomAnchorReadyState((current) => (
          current.scope === openScope && current.generation === openGeneration
            ? current
            : { scope: openScope, generation: openGeneration }
        ))
        return
      }
      frame = requestAnimationFrame(settleBottom)
    }
    settleBottom()
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [
    items.length,
    latestMessageAnchor,
    messageTextSelectionIsActive,
    messageSetAnchor,
    openGeneration,
    openScope,
    scrollToLatest,
    shouldBottomAnchor,
    bottomAnchorReady,
  ])

  useLayoutEffect(() => {
    if (!shouldBottomAnchor) return
    if (!bottomAnchorReady) return
    if (bottomAnchorReleased) return
    if (items.length === 0) return
    if (messageTextSelectionIsActive()) return
    scrollToLatest()
    const frame = requestAnimationFrame(() => {
      if (!messageTextSelectionIsActive()) scrollToLatest()
    })
    return () => cancelAnimationFrame(frame)
  }, [
    bottomAnchorReady,
    bottomAnchorReleased,
    items.length,
    latestMessageAnchor,
    messageTextSelectionIsActive,
    messageSetAnchor,
    scrollToLatest,
    shouldBottomAnchor,
  ])

  // Android Chrome fires VisualViewport changes when the keyboard closes
  // after tapping a search result. Only bottom-anchor ordinary chat opens;
  // search and unread opens have their own intentional scroll target.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return
    function onViewport() {
      requestAnimationFrame(scrollToBottom)
    }
    window.visualViewport.addEventListener('resize', onViewport)
    window.visualViewport.addEventListener('scroll', onViewport)
    return () => {
      window.visualViewport?.removeEventListener('resize', onViewport)
      window.visualViewport?.removeEventListener('scroll', onViewport)
    }
  }, [scrollToBottom])
  const lastUrlScrollTargetRef = useRef<string | null>(null)
  const pendingMessageTargetRef = useRef<string | null>(null)
  const notificationScrollGenerationRef = useRef(0)
  const scrollToMessage = useCallback((messageId: string | null | undefined): boolean => {
    if (!messageId) return false
    const index = items.findIndex((item) => item.kind === 'message' && item.m.id === messageId)
    if (index < 0) return false
    const list = virtuosoRef.current
    if (!list) return false
    list.scrollToIndex({
      index,
      align: 'start',
      behavior: 'auto',
    })
    const row = Array.from(
      scrollerRef.current?.querySelectorAll<HTMLElement>('[data-agentchat-message-id]') ?? [],
    ).find((element) => element.dataset.agentchatMessageId === messageId)
    if (!row || !scrollerRef.current) return false
    row.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' })
    const rowTop = row.getBoundingClientRect().top
    const scrollerTop = scrollerRef.current.getBoundingClientRect().top
    return Math.abs(rowTop - scrollerTop) <= 24
  }, [items])

  useLayoutEffect(() => {
    const target = targetMessageId || null
    if (!target) {
      pendingMessageTargetRef.current = null
      lastUrlScrollTargetRef.current = null
      return
    }
    const key = `${chatId}:${topicId ?? ''}:${target}`
    if (
      lastUrlScrollTargetRef.current === key &&
      pendingMessageTargetRef.current !== target
    ) return
    pendingMessageTargetRef.current = target
    let cancelled = false
    let attempts = 0
    let frame = 0
    const settleTarget = () => {
      if (cancelled) return
      if (pendingMessageTargetRef.current !== target) return
      if (scrollToMessage(target)) {
        lastUrlScrollTargetRef.current = key
        pendingMessageTargetRef.current = null
        return
      }
      attempts += 1
      if (attempts < TARGET_SCROLL_MAX_ATTEMPTS) {
        frame = requestAnimationFrame(settleTarget)
      }
    }
    settleTarget()
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [chatId, scrollToMessage, targetMessageId, topicId])

  useEffect(() => {
    function onNotificationOpen(event: Event) {
      const detail = (event as CustomEvent<{
        chatId?: string
        topicId?: string | null
        messageId?: string | null
      }>).detail
      if (detail?.chatId !== chatId) return
      if ((detail.topicId ?? null) !== (topicId ?? null)) return
      if (detail.messageId) {
        pendingMessageTargetRef.current = detail.messageId
        const generation = ++notificationScrollGenerationRef.current
        let attempts = 0
        const settleNotificationTarget = () => {
          if (notificationScrollGenerationRef.current !== generation) return
          if (scrollToMessage(detail.messageId)) {
            pendingMessageTargetRef.current = null
            return
          }
          attempts += 1
          if (attempts < TARGET_SCROLL_MAX_ATTEMPTS) {
            requestAnimationFrame(settleNotificationTarget)
          }
        }
        requestAnimationFrame(settleNotificationTarget)
        return
      }
      requestAnimationFrame(scrollToLatest)
    }
    window.addEventListener('agentchat:notification-open', onNotificationOpen)
    return () => {
      notificationScrollGenerationRef.current += 1
      window.removeEventListener('agentchat:notification-open', onNotificationOpen)
    }
  }, [chatId, scrollToLatest, scrollToMessage, topicId])

  const renderMessageItem = useCallback((idx: number, item: ListItem) => {
    const itemMessageKey = item.kind === 'message' ? messageStableKey(item.m) : null
    const itemMessageId = item.kind === 'message' ? item.m.id : null
    return (
      <div
        className={clsx(
          'px-3',
          idx === firstItemIndex && 'pt-3',
          idx === firstItemIndex + items.length - 1 && 'pb-3',
          item.kind === 'message' && item.m.id === targetMessageId &&
            'rounded-2xl bg-amber-100/70 ring-2 ring-amber-400/80 dark:bg-amber-400/10 dark:ring-amber-300/60',
        )}
        data-agentchat-message-key={itemMessageKey ?? undefined}
        data-agentchat-message-id={itemMessageId ?? undefined}
      >
        {item.kind === 'separator'
          ? <DaySeparator label={item.label} />
          : <MessageBubble
              m={item.m}
              chatId={chatId}
              isLatestAgent={item.m.id === latestAgentId}
              answeredLater={answeredQuestionIds.has(item.m.id)}
              replacedLater={(messageIndexById.get(item.m.id) ?? Infinity) < latestCardIndex}
              laneIsBusy={laneIsBusy}
              topicHasActiveTurn={Boolean(item.m.topic_id && activeTopicIds.has(item.m.topic_id))}
              onOpenImage={setOpenImage}
              onFork={openForkDialog}
            />}
      </div>
    )
  }, [activeTopicIds, chatId, firstItemIndex, items.length, laneIsBusy, latestAgentId, latestCardIndex, answeredQuestionIds, messageIndexById, openForkDialog, targetMessageId])
  const computeMessageItemKey = useCallback((_idx: number, item: ListItem) => (
    item.kind === 'separator' ? item.id : (item.m.client_id ?? item.m.id)
  ), [])

  if (isLoading) {
    // Skeleton bubbles — looks like a chat that's almost loaded, not an empty
    // spinner. Three rows alternating sender/agent so the layout matches what
    // the real list will look like in 50–200ms.
    return (
      <div className="flex flex-col gap-2 px-3 py-3 overflow-hidden">
        {[
          { isInbound: true,  w: 'w-2/3' },
          { isInbound: false, w: 'w-1/2' },
          { isInbound: false, w: 'w-3/4' },
          { isInbound: true,  w: 'w-1/3' },
        ].map((s, i) => (
          <div
            key={i}
            className={clsx('flex w-full', s.isInbound ? 'justify-end' : 'justify-start')}
          >
            <div
              className={clsx(
                'h-9 rounded-2xl animate-pulse',
                s.w,
                s.isInbound
                  ? 'bg-blue-200/60 dark:bg-blue-900/30'
                  : 'bg-stone-200/70 dark:bg-stone-800/70',
              )}
            />
          </div>
        ))}
      </div>
    )
  }
  if (error && !rawData) {
    const offline = typeof navigator !== 'undefined' && !navigator.onLine
    return (
      <div className="p-6 text-sm text-red-600">
        {offline
          ? 'Offline. This chat has not been cached on this device yet.'
          : `Error: ${String((error as Error).message)}`}
      </div>
    )
  }

  if (msgs.length === 0) {
    if (!loaderResolved) {
      // Loader hasn't reported anything yet for this openScope. Render the
      // same skeleton as the isLoading branch — never flash welcome before
      // we know the topic is genuinely empty.
      return (
        <div className="flex flex-col gap-2 px-3 py-3 overflow-hidden" data-testid="messages-resolving">
          {[
            { isInbound: true,  w: 'w-2/3' },
            { isInbound: false, w: 'w-1/2' },
            { isInbound: false, w: 'w-3/4' },
            { isInbound: true,  w: 'w-1/3' },
          ].map((s, i) => (
            <div
              key={i}
              className={clsx('flex w-full', s.isInbound ? 'justify-end' : 'justify-start')}
            >
              <div
                className={clsx(
                  'h-9 rounded-2xl animate-pulse',
                  s.w,
                  s.isInbound
                    ? 'bg-blue-200/60 dark:bg-blue-900/30'
                    : 'bg-stone-200/70 dark:bg-stone-800/70',
                )}
              />
            </div>
          ))}
        </div>
      )
    }
    return (
      <div className="flex h-full flex-col px-6">
        {hasHiddenPreResetMessages && <HistoryHeader />}
        <div className="grid flex-1 place-items-center">
          <div className="text-center max-w-md">
            <div className="text-4xl mb-3">👋</div>
            <div className="text-lg font-medium mb-1">New conversation</div>
            <div className="text-sm text-stone-500">
              Type a message below to get started, or press <kbd className="px-1.5 py-0.5 mx-0.5 text-xs bg-stone-100 dark:bg-stone-800 rounded border border-stone-300 dark:border-stone-700">/</kbd> to pick a skill.
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Virtualized render — only visible bubbles in the DOM. With 363 msgs in
  // X-Links this drops the per-paint render cost from ~363 bubbles to ~12.
  //
  //   key includes only the semantic route and target intent. Without that,
  //     a previous deliberate search/message target can leave Virtuoso parked
  //     hundreds of messages up when the URL later clears message_id.
  //     Browser history keys are intentionally excluded: location.key churn
  //     is not a chat-open intent and remounting Virtuoso for it causes a
  //     visible blank/top-jump during ordinary navigation.
  //   key=`${chatId}:${topicId}:${openScope}` — remount on chat/topic switch so
  //     initialTopMostItemIndex re-applies and the new context starts from
  //     an explicit scroll intent, not Virtuoso's previous scroll state.
  //   initialTopMostItemIndex uses alignment objects, not bare indexes:
  //     every ordinary chat/topic open lands at the real bottom (the latest
  //     message) and the bottom-anchor settle holds it there as content loads —
  //     dead simple, and it cannot hop because nothing scrolls it away after
  //     paint. Only search/deep-link opens (targetItemIndex) center the target.
  //     A bare last index aligns the top of a long final bubble, which
  //     looks like opening miles above the bottom, so use align:'end'.
  //   followOutput="auto" — scrolls to bottom iff user was at bottom.
  //     Per-item ResizeObserver (built into Virtuoso) re-measures the
  //     last bubble as streaming deltas grow its body, keeping pin tight.
  //   atBottomThreshold=64 — preserves the 64px tolerance the manual
  //     scroll listener used pre-virtualization.
  //   increaseViewportBy={top:200,bottom:200} — render a strip of items
  //     just outside the viewport so streaming growth + scroll-up reveal
  //     are smooth (no white flash when items enter).
  //   overflowAnchor:'none' — disable the browser's native scroll
  //     anchoring so Virtuoso owns positioning unambiguously.
  // Interleave day-separator pills between bubbles. Cheap to recompute
  // every render — msgs is already in memory and we visit each once.
  return (
    <>
      <Virtuoso
        key={openScope}
        ref={virtuosoRef}
        scrollerRef={(el) => {
          scrollerRef.current = el as HTMLElement | null
          if (el) {
            const scroller = el as HTMLElement
            scroller.dataset.agentchatMessageScroller = 'true'
            scroller.style.removeProperty('visibility')
          }
        }}
        data={items}
        firstItemIndex={firstItemIndex}
        initialTopMostItemIndex={
          targetItemIndex >= 0
            ? { index: targetItemIndex, align: 'start' }
            : { index: 'LAST', align: 'end' }
        }
        followOutput={(isAtBottom) => {
          if (bottomAnchorReleased || historyRequestRef.current.pending || historyRestoringRef.current) return false
          if (messageTextSelectionIsActive()) return false
          return isAtBottom ? 'auto' : false
        }}
        atBottomThreshold={64}
        atBottomStateChange={(b) => {
          if (historyRequestRef.current.pending || historyRestoringRef.current) return
          if (b) {
            wasAtBottomRef.current = true
            setUserReleasedBottomAnchor((current) => (
              current.scope === openScope && !current.released
                ? current
                : { scope: openScope, released: false }
            ))
          } else if (bottomAnchorReady) {
            wasAtBottomRef.current = false
          }
        }}
        increaseViewportBy={{ top: 200, bottom: 200 }}
        itemContent={renderMessageItem}
        components={{
          Header: HistoryHeader,
        }}
        // Stable key across the optimistic→server-confirm id swap. On the
        // initial optimistic insert we get `temp-…` for both `id` and
        // `client_id`. When the POST returns, useSendMessage swaps `id`
        // to the real server uuid but keeps `client_id` as the temp value.
        // Keying by `client_id ?? id` means Virtuoso sees ONE row across
        // the swap — no DOM unmount/remount, no flicker. Real messages
        // fetched cold (no client_id) just key by id, same as before.
        // Day separators key on their own stable `sep-YYYY-MM-DD` id.
        computeItemKey={computeMessageItemKey}
        className="h-full"
        // overflow-x:hidden hard-stops the horizontal swipe Mark saw
        // during a streaming turn — long unbroken model output (URLs,
        // code, paths) was causing transient layout overflow that the
        // virtualised scroller was treating as horizontally pannable.
        // The vertical scroll is what we want; horizontal is never
        // useful here. Horizontal padding belongs on item rows, not this
        // scroller: Virtuoso measures its internal item layer at full
        // viewport width, so scroller padding can push right-aligned
        // bubbles under this hidden edge. ``overflowAnchor: none`` stays
        // — Virtuoso owns the vertical anchor.
        style={{
          overflowAnchor: 'none',
          overflowX: 'hidden',
        }}
      />
      {openImage && (
        <ImageAttachmentViewer
          att={openImage}
          onClose={() => setOpenImage(null)}
        />
      )}
      {forkingMessage && (
        <ForkMessageDialog
          mode={forkMode}
          freeChats={freeForkChats}
          topicTitle={forkTopicTitle}
          setTopicTitle={setForkTopicTitle}
          pending={forkMessage.isPending}
          error={forkError}
          onCancel={closeForkDialog}
          onForkToChat={(targetChatId) => { void forkToChat(targetChatId) }}
          onForkToTopic={() => { void forkToTopic() }}
        />
      )}
    </>
  )
}
